import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { adminAPI } from '../services/api';

function InfoTooltip({ text }) {
  const [show, setShow]     = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref                 = useRef(null);

  const TOOLTIP_W = 270;

  const handleEnter = () => {
    if (ref.current) {
      const rect   = ref.current.getBoundingClientRect();
      // Preferred: open above the button, right-aligned to button
      let left = rect.right - TOOLTIP_W;
      let top  = rect.top - 8; // will be adjusted by transform below

      // If opening left would clip the left edge, clamp to 8px from screen left
      if (left < 8) left = 8;
      // If opening above would clip top, open below instead
      const openBelow = rect.top < 160;

      setCoords({ left, top: openBelow ? rect.bottom + 6 : rect.top - 6, openBelow });
    }
    setShow(true);
  };

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle', marginLeft: 5 }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 15, height: 15, borderRadius: '50%', border: '1.5px solid #4CAF7D',
        color: '#1B5E3B', fontSize: 9, fontWeight: 700, cursor: 'default',
        background: '#E8F6EE', lineHeight: 1, userSelect: 'none', flexShrink: 0,
      }}>i</span>
      {show && (
        <div style={{
          position: 'fixed',
          top: coords.openBelow ? coords.top : undefined,
          bottom: coords.openBelow ? undefined : `calc(100vh - ${coords.top}px)`,
          left: coords.left,
          zIndex: 9999,
          background: '#1a1a2e',
          color: '#e2e8f0',
          fontSize: 12,
          lineHeight: 1.6,
          borderRadius: 10,
          padding: '10px 13px',
          width: TOOLTIP_W,
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
          border: '1px solid #312e81',
          pointerEvents: 'none',
          whiteSpace: 'pre-line',
        }}>
          {text}
        </div>
      )}
    </span>
  );
}

// ── New-company onboarding stepper ───────────────────────────────────────────
const SETUP_STEPS = [
  { n: 1, label: 'Connect Database' },
  { n: 2, label: 'Upload Schema'    },
  { n: 3, label: 'Semantic Map'     },
];

function OnboardingBanner({ step }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #1e1040, #0A2E17)',
      borderRadius: 12, padding: '12px 18px', marginBottom: 22,
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      border: '1px solid #4c1d95',
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#81C784', letterSpacing: '0.1em', textTransform: 'uppercase', marginRight: 8, whiteSpace: 'nowrap' }}>
        New Company Setup
      </span>
      {SETUP_STEPS.map((s, i) => (
        <React.Fragment key={s.n}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: s.n < step ? '#059669' : s.n === step ? '#1B5E3B' : 'rgba(255,255,255,0.07)',
              border: `2px solid ${s.n < step ? '#059669' : s.n === step ? '#4CAF7D' : 'rgba(255,255,255,0.18)'}`,
              fontSize: 10, fontWeight: 800, color: '#fff', flexShrink: 0,
            }}>
              {s.n < step ? '✓' : s.n}
            </div>
            <span style={{
              fontSize: 12, fontWeight: s.n === step ? 700 : 400,
              color: s.n < step ? '#6ee7b7' : s.n === step ? '#C8EDD6' : 'rgba(255,255,255,0.35)',
              whiteSpace: 'nowrap',
            }}>{s.label}</span>
          </div>
          {i < 2 && <div style={{ width: 20, height: 1, background: 'rgba(255,255,255,0.15)', flexShrink: 0, margin: '0 2px' }} />}
        </React.Fragment>
      ))}
      <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>Step {step} of 3</span>
    </div>
  );
}

const TABS = [
  { id: 'config',    label: 'Model Config',     icon: '⚙️' },
  { id: 'users',     label: 'User Management',  icon: '👥' },
  { id: 'analytics', label: 'Usage Analytics',  icon: '📊' },
  { id: 'security',  label: 'Security',         icon: '🔒' },
  { id: 'schema',    label: 'Schema & Semantic', icon: '🗂️' },
  { id: 'database',  label: 'Database',          icon: '🗃️' },
  { id: 'ai-config', label: 'AI Config',         icon: '🤖' },
];

const MODELS = [
  { value: 'gpt-4o',           label: 'GPT-4o (Recommended)' },
  { value: 'gpt-4o-mini',      label: 'GPT-4o Mini (Faster / Cheaper)' },
  { value: 'gpt-4-turbo',      label: 'GPT-4 Turbo' },
  { value: 'gpt-4',            label: 'GPT-4' },
  { value: 'gpt-3.5-turbo',    label: 'GPT-3.5 Turbo (Fastest)' },
];

// ── Model Config Tab ──────────────────────────────────────────────────────

function ModelConfigTab() {
  const [form, setForm]         = useState({ openai_model: 'gpt-4o', temperature: 0.1, max_tokens: 800, conversation_history_enabled: true, openai_api_key: '' });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState('');
  const [showKey, setShowKey]   = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminAPI.getConfig();
      setForm(f => ({ ...f, ...res.data, openai_api_key: '' }));
    } catch { setError('Failed to load configuration.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      const payload = {
        openai_model: form.openai_model,
        temperature: parseFloat(form.temperature),
        max_tokens: parseInt(form.max_tokens),
        conversation_history_enabled: form.conversation_history_enabled,
      };
      if (form.openai_api_key.trim()) payload.openai_api_key = form.openai_api_key.trim();
      await adminAPI.updateConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save configuration.');
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (loading) return <div style={S.center}><Spinner /><span style={{ color: '#9b9b9b', marginLeft: 12 }}>Loading config…</span></div>;

  return (
    <form onSubmit={handleSave} style={S.configWrap}>
      <div style={S.sectionHeader}>
        <div>
          <div style={S.sectionTitle}>Model Configuration</div>
          <div style={S.sectionSub}>Changes apply to all queries immediately — no server restart needed.</div>
        </div>
        <button type="submit" disabled={saving} style={{ ...S.saveBtn, opacity: saving ? 0.8 : 1 }}>
          {saving ? <><Spinner small /> Saving…</> : saved ? '✓ Saved!' : 'Save Changes'}
        </button>
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}
      {saved && <div style={S.successBanner}>✓ Configuration saved. Next query will use the updated settings.</div>}

      <div style={S.grid2}>
        {/* Model selector */}
        <div style={{ ...S.card, gridColumn: '1 / -1' }}>
          <div style={S.cardTitle}>AI Model</div>
          <div style={S.cardSub}>The OpenAI model used for SQL generation.</div>
          <div style={S.modelGrid}>
            {MODELS.map(m => (
              <label key={m.value} style={{ ...S.modelOption, ...(form.openai_model === m.value ? S.modelOptionActive : {}) }}>
                <input type="radio" name="model" value={m.value} checked={form.openai_model === m.value}
                  onChange={() => set('openai_model', m.value)} style={{ display: 'none' }} />
                <div style={S.modelRadio}>
                  <div style={{ ...S.modelDot, ...(form.openai_model === m.value ? S.modelDotActive : {}) }} />
                </div>
                <div>
                  <div style={S.modelLabel}>{m.value}</div>
                  <div style={S.modelDesc}>{m.label.replace(`${m.value} `, '')}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Temperature */}
        <div style={S.card}>
          <div style={S.cardTitle}>Temperature <span style={S.valueChip}>{Number(form.temperature).toFixed(2)}</span></div>
          <div style={S.cardSub}>Controls creativity. Lower = more focused SQL. Higher = more varied responses.</div>
          <div style={{ margin: '16px 0 4px' }}>
            <input type="range" min="0" max="1" step="0.05"
              value={form.temperature}
              onChange={e => set('temperature', parseFloat(e.target.value))}
              style={S.slider}
            />
            <div style={S.sliderLabels}><span>Precise (0.0)</span><span>Creative (1.0)</span></div>
          </div>
          <div style={S.presetRow}>
            {[0.0, 0.1, 0.3, 0.5, 0.7].map(v => (
              <button key={v} type="button" onClick={() => set('temperature', v)}
                style={{ ...S.presetBtn, ...(form.temperature === v ? S.presetBtnActive : {}) }}>
                {v.toFixed(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Max Tokens */}
        <div style={S.card}>
          <div style={S.cardTitle}>Max Tokens <span style={S.valueChip}>{form.max_tokens}</span></div>
          <div style={S.cardSub}>Maximum tokens in the SQL generation response. Higher = longer SQL allowed.</div>
          <div style={{ margin: '16px 0 4px' }}>
            <input type="range" min="200" max="4000" step="100"
              value={form.max_tokens}
              onChange={e => set('max_tokens', parseInt(e.target.value))}
              style={S.slider}
            />
            <div style={S.sliderLabels}><span>200</span><span>4000</span></div>
          </div>
          <div style={S.presetRow}>
            {[400, 800, 1200, 2000, 4000].map(v => (
              <button key={v} type="button" onClick={() => set('max_tokens', v)}
                style={{ ...S.presetBtn, ...(form.max_tokens === v ? S.presetBtnActive : {}) }}>
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation History */}
        <div style={S.card}>
          <div style={S.cardTitle}>Conversation History</div>
          <div style={S.cardSub}>When enabled, follow-up queries use context from previous questions in the session.</div>
          <div style={S.toggleRow}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: form.conversation_history_enabled ? '#059669' : '#9b9b9b' }}>
                {form.conversation_history_enabled ? 'Enabled' : 'Disabled'}
              </div>
              <div style={{ fontSize: 12, color: '#9b9b9b', marginTop: 2 }}>
                {form.conversation_history_enabled ? 'Users can ask follow-up questions' : 'Every query is treated as standalone'}
              </div>
            </div>
            <button type="button" onClick={() => set('conversation_history_enabled', !form.conversation_history_enabled)}
              style={{ ...S.toggle, background: form.conversation_history_enabled ? '#059669' : '#e5e5e5' }}>
              <div style={{ ...S.toggleThumb, transform: form.conversation_history_enabled ? 'translateX(22px)' : 'translateX(2px)' }} />
            </button>
          </div>
        </div>

        {/* API Key */}
        <div style={S.card}>
          <div style={S.cardTitle}>OpenAI API Key</div>
          <div style={S.cardSub}>
            {form.api_key_configured
              ? 'An API key is currently configured. Enter a new one to replace it.'
              : 'No API key configured. Enter one below.'}
          </div>
          <div style={S.keyRow}>
            <input
              type={showKey ? 'text' : 'password'}
              value={form.openai_api_key}
              onChange={e => set('openai_api_key', e.target.value)}
              placeholder={form.api_key_configured ? '••••••••••••  (leave blank to keep current)' : 'sk-proj-...'}
              style={S.keyInput}
            />
            <button type="button" onClick={() => setShowKey(s => !s)} style={S.eyeBtn}>
              {showKey
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9b9b9b" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9b9b9b" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
          {form.api_key_configured && <div style={S.keyHint}>✓ Key is set</div>}
        </div>
      </div>
    </form>
  );
}

// ── User Management Tab ───────────────────────────────────────────────────

const ROLES = ['admin', 'analyst', 'viewer', 'user'];
const ROLE_COLORS = { admin: '#b45309', analyst: '#1B5E3B', viewer: '#059669', user: '#6b7280' };

function UserManagementTab() {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newPw, setNewPw]         = useState('');
  const [form, setForm]           = useState({ name: '', email: '', password: '', role: 'user' });
  const [msg, setMsg]             = useState({ text: '', ok: true });
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [approving, setApproving] = useState({});

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: '', ok: true }), 3000); };

  const load = useCallback(async () => {
    try { const r = await adminAPI.listUsers(); setUsers(r.data.users); }
    catch { flash('Failed to load users.', false); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await adminAPI.createUser(form); flash('User created.'); setShowCreate(false); setForm({ name:'', email:'', password:'', role:'user' }); load(); }
    catch (err) { flash(err.response?.data?.error || 'Failed to create user.', false); }
    finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try { await adminAPI.updateUser(u.id, { is_active: !u.is_active }); flash(`User ${u.is_active ? 'deactivated' : 'activated'}.`); load(); }
    catch { flash('Failed to update user.', false); }
  };

  const changeRole = async (u, role) => {
    try { await adminAPI.updateUser(u.id, { role }); flash('Role updated.'); load(); }
    catch { flash('Failed to update role.', false); }
  };

  const handleReset = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await adminAPI.resetPassword(resetTarget.id, newPw); flash('Password reset.'); setResetTarget(null); setNewPw(''); }
    catch (err) { flash(err.response?.data?.error || 'Failed to reset.', false); }
    finally { setSaving(false); }
  };

  const handleApprove = async (u) => {
    setApproving(a => ({ ...a, [u.id]: 'approving' }));
    try { await adminAPI.approveUser(u.id); flash(`${u.name} approved — they can now sign in.`); load(); }
    catch { flash('Failed to approve user.', false); }
    finally { setApproving(a => { const n = { ...a }; delete n[u.id]; return n; }); }
  };

  const handleReject = async (u) => {
    setApproving(a => ({ ...a, [u.id]: 'rejecting' }));
    try { await adminAPI.rejectUser(u.id); flash(`${u.name}'s request rejected.`); load(); }
    catch { flash('Failed to reject user.', false); }
    finally { setApproving(a => { const n = { ...a }; delete n[u.id]; return n; }); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminAPI.deleteUser(deleteTarget.id);
      flash(`${deleteTarget.name} deleted permanently.`);
      setDeleteTarget(null);
      load();
    } catch (err) { flash(err.response?.data?.error || 'Failed to delete user.', false); }
    finally { setDeleting(false); }
  };

  const pending  = users.filter(u => (u.approval_status || 'approved') === 'pending');
  const approved = users.filter(u => (u.approval_status || 'approved') !== 'pending');

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={S.sectionHeader}>
        <div>
          <div style={S.sectionTitle}>User Management</div>
          <div style={S.sectionSub}>Approve sign-up requests, create users, assign roles, and manage access.</div>
        </div>
        <button onClick={() => setShowCreate(s => !s)} style={S.saveBtn}>
          {showCreate ? 'Cancel' : '+ New User'}
        </button>
      </div>

      {msg.text && <div style={{ ...S.successBanner, ...(msg.ok ? {} : S.errorBanner) }}>{msg.text}</div>}

      {/* ── Pending Approval requests ── */}
      {!loading && pending.length > 0 && (
        <div style={{ marginBottom: 24, border: '1.5px solid #fde68a', borderRadius: 12, overflow: 'hidden', background: '#fffbeb' }}>
          <div style={{ padding: '12px 18px', background: '#fef3c7', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>⏳</span>
            <div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>Pending Approval</span>
              <span style={{ fontSize: 12, color: '#b45309', marginLeft: 8 }}>{pending.length} request{pending.length !== 1 ? 's' : ''} waiting</span>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #fde68a', background: '#fffdf5' }}>
                {['User','Email','Registered','Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pending.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #fef3c7' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1a1a2e' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#d97706,#f59e0b)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {u.name}
                          {u.telegram_user_id && (
                            <span style={{ fontSize: 10, fontWeight: 700, background: '#E3F2FD', color: '#1565C0', borderRadius: 4, padding: '1px 6px' }}>
                              ✈️ Telegram
                            </span>
                          )}
                        </div>
                        {u.telegram_username && (
                          <div style={{ fontSize: 11, color: '#1565C0', marginTop: 1 }}>{u.telegram_username}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#6b7280' }}>
                    {u.telegram_user_id ? (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }}>via Telegram</span>
                    ) : u.email}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#9b9b9b', fontSize: 12 }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => handleApprove(u)}
                        disabled={!!approving[u.id]}
                        style={{ ...S.actBtn, background: '#ecfdf5', color: '#059669', borderColor: '#6ee7b7', fontWeight: 700, padding: '5px 14px' }}>
                        {approving[u.id] === 'approving' ? 'Approving…' : '✓ Approve'}
                      </button>
                      <button
                        onClick={() => handleReject(u)}
                        disabled={!!approving[u.id]}
                        style={{ ...S.actBtn, color: '#dc2626', borderColor: '#fca5a5', padding: '5px 14px' }}>
                        {approving[u.id] === 'rejecting' ? 'Rejecting…' : '✕ Reject'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} style={{ ...S.card, marginBottom: 20 }}>
          <div style={S.cardTitle}>New User</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
            {[['Full name','text','name'],['Email','email','email'],['Password','password','password']].map(([lbl,type,key]) => (
              <div key={key} style={S.field}>
                <label style={S.flabel}>{lbl}</label>
                <input type={type} required value={form[key]} onChange={e => setForm(f=>({...f,[key]:e.target.value}))} style={S.finput} />
              </div>
            ))}
            <div style={S.field}>
              <label style={S.flabel}>Role</label>
              <select value={form.role} onChange={e => setForm(f=>({...f,role:e.target.value}))} style={S.finput}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" disabled={saving} style={{ ...S.saveBtn, marginTop: 14 }}>
            {saving ? 'Creating…' : 'Create User'}
          </button>
        </form>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div style={S.modalOverlay}>
          <form onSubmit={handleReset} style={S.modal}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: '#1a1a2e' }}>Reset Password</div>
            <div style={{ fontSize: 13, color: '#9b9b9b', marginBottom: 16 }}>for {resetTarget.name}</div>
            <input type="password" required minLength={6} placeholder="New password (min 6 chars)"
              value={newPw} onChange={e => setNewPw(e.target.value)} style={S.finput} />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="submit" disabled={saving} style={S.saveBtn}>{saving ? 'Saving…' : 'Reset'}</button>
              <button type="button" onClick={() => { setResetTarget(null); setNewPw(''); }} style={S.cancelBtn}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Delete user confirmation modal */}
      {deleteTarget && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: '#dc2626' }}>⚠️ Delete User</div>
            <div style={{ fontSize: 13, color: '#1a1a2e', marginBottom: 6 }}>
              You are about to permanently delete <strong>{deleteTarget.name}</strong>.
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16, lineHeight: 1.6 }}>
              This will remove them from the database and revoke all access — including web portal and Telegram bot.
              {deleteTarget.telegram_user_id && (
                <span style={{ display: 'block', marginTop: 6, color: '#1565C0' }}>
                  ✈️ This user signed up via Telegram. Their bot session will also be cleared.
                </span>
              )}
              <strong style={{ display: 'block', marginTop: 8, color: '#dc2626' }}>This action cannot be undone.</strong>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
              <button type="button" onClick={() => setDeleteTarget(null)} style={S.cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Users table (approved + rejected) */}
      {loading ? <div style={S.center}><Spinner /></div> : (
        <div style={S.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #D1F0E0' }}>
                {['User','Email','Role','Status','Last Login','Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {approved.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #E8F6EE' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1a1a2e' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#1B5E3B,#2E7D52)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      {u.name}
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>{u.email}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <select value={u.role || 'user'} onChange={e => changeRole(u, e.target.value)}
                      style={{ fontSize: 12, fontWeight: 600, color: ROLE_COLORS[u.role] || '#6b7280', border: '1px solid #D1F0E0', borderRadius: 6, padding: '3px 8px', background: '#f2faf8', cursor: 'pointer' }}>
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: u.is_active ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)', color: u.is_active ? '#059669' : '#dc2626' }}>
                      {u.approval_status === 'rejected' ? 'Rejected' : u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#9b9b9b', fontSize: 12 }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button onClick={() => setResetTarget(u)} style={S.actBtn}>Reset PW</button>
                      <button onClick={() => toggleActive(u)} style={{ ...S.actBtn, color: u.is_active ? '#dc2626' : '#059669', borderColor: u.is_active ? '#fca5a5' : '#6ee7b7' }}>
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => setDeleteTarget(u)} style={{ ...S.actBtn, color: '#dc2626', borderColor: '#fca5a5', fontWeight: 700 }}>
                        🗑 Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {approved.length === 0 && <div style={{ textAlign: 'center', color: '#9b9b9b', padding: 32 }}>No users found.</div>}
        </div>
      )}
    </div>
  );
}

// ── Usage Analytics Tab ───────────────────────────────────────────────────

function AnalyticsTab() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // History state
  const [history,  setHistory]  = useState([]);
  const [hTotal,   setHTotal]   = useState(0);
  const [hPage,    setHPage]    = useState(1);
  const [hLoading, setHLoading] = useState(false);
  const [filters,  setFilters]  = useState({ user: '', intent: '', status: '' });
  const [pending,  setPending]  = useState({ user: '', intent: '', status: '' });

  const load = useCallback(async () => {
    try {
      const res = await adminAPI.getUsageAnalytics();
      setData(res.data);
    } catch { setError('Failed to load analytics.'); }
    finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async (page, f) => {
    setHLoading(true);
    try {
      const params = { page, limit: 20, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
      const res = await adminAPI.getQueryHistory(params);
      setHistory(res.data.history);
      setHTotal(res.data.total);
    } catch {} finally { setHLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(hPage, filters); }, [hPage, filters, loadHistory]);

  // Auto-refresh history when user navigates back to this tab
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadHistory(hPage, filters); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [hPage, filters, loadHistory]);

  const applyFilters = () => { setFilters(pending); setHPage(1); };
  const clearFilters = () => { const e = { user: '', intent: '', status: '' }; setPending(e); setFilters(e); setHPage(1); };

  if (loading) return <div style={S.center}><Spinner /></div>;
  if (error)   return <div style={S.errorBanner}>{error}</div>;
  if (!data)   return null;

  const { summary, by_day, top_intents, top_users } = data;
  const totalPages = Math.ceil(hTotal / 20);

  const kpis = [
    { label: 'Total Queries', value: summary.total_queries.toLocaleString(), sub: `↑ ${summary.today} today`, color: '#1B5E3B' },
    { label: 'This Week',     value: summary.this_week.toLocaleString(),   sub: `${summary.this_month} this month`, color: '#2563eb' },
    { label: 'Success Rate',  value: `${(100 - summary.error_rate).toFixed(1)}%`, sub: `${summary.failed_queries} failed`, color: '#059669' },
    { label: 'Avg Response',  value: `${summary.avg_response_ms}ms`,        sub: `${summary.active_users} active users`, color: '#d97706' },
  ];

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ ...S.sectionHeader, marginBottom: 24 }}>
        <div>
          <div style={S.sectionTitle}>Usage Analytics</div>
          <div style={S.sectionSub}>Query counts, response times, error rates, and user activity</div>
        </div>
        <button onClick={load} style={{ ...S.actBtn, padding: '7px 16px', fontSize: 13 }}>↻ Refresh</button>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ ...S.card, borderTop: `3px solid ${k.color}` }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: k.color, marginBottom: 4 }}>{k.value}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{k.label}</div>
            <div style={{ fontSize: 11, color: '#9b9b9b', marginTop: 2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* Queries per day */}
        <div style={S.card}>
          <div style={{ ...S.cardTitle, marginBottom: 16 }}>Queries — last 14 days</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={by_day} margin={{ top: 0, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0edf8" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9b9b9b' }}
                tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: '#9b9b9b' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #D1F0E0' }}
                formatter={(v, name) => [v, name === 'total' ? 'Total' : 'Errors']}
                labelFormatter={l => `Date: ${l}`}
              />
              <Bar dataKey="total"  fill="#1B5E3B" radius={[4,4,0,0]} maxBarSize={28} />
              <Bar dataKey="errors" fill="#fca5a5" radius={[4,4,0,0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top intents */}
        <div style={S.card}>
          <div style={{ ...S.cardTitle, marginBottom: 12 }}>Top Intents</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {top_intents.slice(0, 6).map(r => {
              const max = top_intents[0]?.count || 1;
              return (
                <div key={r.intent}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                    <span style={{ color: '#374151', fontWeight: 500, textTransform: 'capitalize' }}>{r.intent.replace(/_/g, ' ')}</span>
                    <span style={{ color: '#1B5E3B', fontWeight: 700 }}>{r.count}</span>
                  </div>
                  <div style={{ height: 5, background: '#D1F0E0', borderRadius: 3 }}>
                    <div style={{ height: 5, width: `${(r.count / max) * 100}%`, background: '#1B5E3B', borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top users */}
        <div style={S.card}>
          <div style={{ ...S.cardTitle, marginBottom: 12 }}>Most Active Users</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {top_users.slice(0, 6).map((r, i) => (
              <div key={r.user_email} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: i === 0 ? '#1B5E3B' : '#D1F0E0', color: i === 0 ? '#fff' : '#1B5E3B', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.user_email}</div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1B5E3B', flexShrink: 0 }}>{r.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Query History table */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={S.cardTitle}>Query History</div>
          <button onClick={() => loadHistory(hPage, filters)} style={{ ...S.actBtn, padding: '5px 14px', fontSize: 12 }}>↻ Refresh</button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={S.field}>
            <div style={S.flabel}>User email</div>
            <input style={{ ...S.finput, width: 160 }} value={pending.user}
              onChange={e => setPending(p => ({ ...p, user: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
              placeholder="filter by email" />
          </div>
          <div style={S.field}>
            <div style={S.flabel}>Intent</div>
            <input style={{ ...S.finput, width: 130 }} value={pending.intent}
              onChange={e => setPending(p => ({ ...p, intent: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
              placeholder="e.g. sales" />
          </div>
          <div style={S.field}>
            <div style={S.flabel}>Status</div>
            <select style={{ ...S.finput, width: 110 }} value={pending.status}
              onChange={e => setPending(p => ({ ...p, status: e.target.value }))}>
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
            </select>
          </div>
          <button onClick={applyFilters} style={{ ...S.saveBtn, padding: '9px 18px', fontSize: 13 }}>Search</button>
          {(filters.user || filters.intent || filters.status) && (
            <button onClick={clearFilters} style={{ ...S.cancelBtn, fontSize: 13 }}>Clear</button>
          )}
        </div>

        {/* Table */}
        {hLoading ? <div style={S.center}><Spinner /></div> : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #D1F0E0' }}>
                    {['Query', 'Intent', 'User', 'Status', 'Rows', 'Time (ms)', 'Date'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#9b9b9b' }}>No queries found</td></tr>
                  ) : history.map(row => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #f4f2fa' }}>
                      <td style={{ padding: '8px 10px', color: '#1a1a2e', maxWidth: 280 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.query_text}>{row.query_text}</div>
                      </td>
                      <td style={{ padding: '8px 10px', color: '#6b7280', textTransform: 'capitalize' }}>{(row.intent || '—').replace(/_/g, ' ')}</td>
                      <td style={{ padding: '8px 10px', color: '#6b7280', maxWidth: 160 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.user_email || '—'}</div>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, background: row.status === 'error' ? 'rgba(220,38,38,0.1)' : 'rgba(5,150,105,0.1)', color: row.status === 'error' ? '#dc2626' : '#059669' }}>
                          {row.status || 'success'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', color: '#6b7280' }}>{row.row_count ?? '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#6b7280' }}>{row.execution_ms ?? '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#9b9b9b', whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, fontSize: 12, color: '#6b7280' }}>
                <span>Showing {((hPage - 1) * 20) + 1}–{Math.min(hPage * 20, hTotal)} of {hTotal}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setHPage(p => p - 1)} disabled={hPage === 1} style={{ ...S.actBtn, opacity: hPage === 1 ? 0.4 : 1 }}>← Prev</button>
                  <span style={{ padding: '4px 10px', fontWeight: 600, color: '#1B5E3B' }}>Page {hPage}/{totalPages}</span>
                  <button onClick={() => setHPage(p => p + 1)} disabled={hPage === totalPages} style={{ ...S.actBtn, opacity: hPage === totalPages ? 0.4 : 1 }}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Security Tab ─────────────────────────────────────────────────────────

function SecurityTab() {
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');
  const [newKw,   setNewKw]   = useState('');

  const load = useCallback(async () => {
    try {
      const res = await adminAPI.getSecurityConfig();
      setForm(res.data);
    } catch { setError('Failed to load security config.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addKeyword = () => {
    const kw = newKw.trim().toUpperCase();
    if (!kw || form.blocked_keywords.includes(kw)) return;
    setForm(f => ({ ...f, blocked_keywords: [...f.blocked_keywords, kw] }));
    setNewKw('');
  };

  const removeKeyword = (kw) =>
    setForm(f => ({ ...f, blocked_keywords: f.blocked_keywords.filter(k => k !== kw) }));

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await adminAPI.updateSecurityConfig(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { setError('Failed to save.'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={S.center}><Spinner /></div>;
  if (!form)   return <div style={S.errorBanner}>{error}</div>;

  return (
    <div style={{ maxWidth: 780 }}>
      <div style={{ ...S.sectionHeader, marginBottom: 24 }}>
        <div>
          <div style={S.sectionTitle}>Security & Query Control</div>
          <div style={S.sectionSub}>Blocked keywords, row limits, and per-user rate limiting</div>
        </div>
        <button onClick={handleSave} disabled={saving} style={S.saveBtn}>
          {saving ? <Spinner small /> : '💾'} {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {error  && <div style={S.errorBanner}>{error}</div>}
      {saved  && <div style={S.successBanner}>Security settings saved successfully.</div>}

      {/* Blocked Keywords */}
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={S.cardTitle}>🚫 Blocked Keywords
          <span style={{ ...S.valueChip, marginLeft: 8 }}>{form.blocked_keywords.length}</span>
        </div>
        <div style={{ ...S.cardSub, marginBottom: 14 }}>
          These SQL keywords are blocked in every generated query. Changes apply immediately — no restart needed.
        </div>

        {/* Add keyword */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            style={{ ...S.finput, flex: 1, textTransform: 'uppercase' }}
            value={newKw}
            onChange={e => setNewKw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addKeyword()}
            placeholder="e.g. MERGE"
          />
          <button onClick={addKeyword} style={{ ...S.saveBtn, padding: '9px 18px', fontSize: 13 }}>+ Add</button>
        </div>

        {/* Keyword chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {form.blocked_keywords.map(kw => (
            <div key={kw} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 7, background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)', fontSize: 12, fontWeight: 600, color: '#dc2626' }}>
              {kw}
              <button onClick={() => removeKeyword(kw)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
            </div>
          ))}
        </div>
      </div>

      {/* Max Rows */}
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={S.cardTitle}>📦 Max Rows per Query</div>
        <div style={{ ...S.cardSub, marginBottom: 14 }}>Cap how many rows any query can return. Prevents runaway queries from overloading the UI.</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <input
            type="range" min={50} max={5000} step={50}
            value={form.max_rows}
            onChange={e => setForm(f => ({ ...f, max_rows: parseInt(e.target.value) }))}
            style={{ ...S.slider, flex: 1 }}
          />
          <span style={{ ...S.valueChip, minWidth: 60, textAlign: 'center' }}>{form.max_rows.toLocaleString()} rows</span>
        </div>
        <div style={S.sliderLabels}><span>50</span><span>1,000</span><span>2,500</span><span>5,000</span></div>
        <div style={{ ...S.presetRow, marginTop: 10 }}>
          {[100, 500, 1000, 2000].map(v => (
            <button key={v} onClick={() => setForm(f => ({ ...f, max_rows: v }))}
              style={{ ...S.presetBtn, ...(form.max_rows === v ? S.presetBtnActive : {}) }}>
              {v.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {/* Rate Limiting */}
      <div style={S.card}>
        <div style={S.cardTitle}>⏱️ Rate Limiting</div>
        <div style={{ ...S.cardSub, marginBottom: 14 }}>Limit how many queries each user can run per day. Resets at midnight.</div>

        <div style={S.toggleRow}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Enable rate limiting</span>
          <button
            onClick={() => setForm(f => ({ ...f, rate_limit_enabled: !f.rate_limit_enabled }))}
            style={{ ...S.toggle, background: form.rate_limit_enabled ? '#1B5E3B' : '#e5e7eb' }}>
            <div style={{ ...S.toggleThumb, transform: form.rate_limit_enabled ? 'translateX(20px)' : 'translateX(3px)' }} />
          </button>
        </div>

        {form.rate_limit_enabled && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 10 }}>
              Max queries per user per day
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <input
                type="range" min={10} max={2000} step={10}
                value={form.max_queries_per_day}
                onChange={e => setForm(f => ({ ...f, max_queries_per_day: parseInt(e.target.value) }))}
                style={{ ...S.slider, flex: 1 }}
              />
              <span style={{ ...S.valueChip, minWidth: 72, textAlign: 'center' }}>{form.max_queries_per_day}/day</span>
            </div>
            <div style={S.sliderLabels}><span>10</span><span>500</span><span>1,000</span><span>2,000</span></div>
            <div style={{ ...S.presetRow, marginTop: 10 }}>
              {[50, 100, 250, 500].map(v => (
                <button key={v} onClick={() => setForm(f => ({ ...f, max_queries_per_day: v }))}
                  style={{ ...S.presetBtn, ...(form.max_queries_per_day === v ? S.presetBtnActive : {}) }}>
                  {v}/day
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Schema & Semantic Tab ─────────────────────────────────────────────────

const MODULE_COLORS = {
  Sales:       { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
  Purchase:    { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
  Production:  { bg: '#fff7ed', border: '#fed7aa', text: '#9a3412' },
  Jobwork:     { bg: '#fdf4ff', border: '#f5d0fe', text: '#86198f' },
  Accounts:    { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46' },
  Inventory:   { bg: '#fff1f2', border: '#fecdd3', text: '#9f1239' },
  QC:          { bg: '#fefce8', border: '#fef08a', text: '#854d0e' },
  Maintenance: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
  Master:      { bg: '#f0fdf4', border: '#C8EDD6', text: '#6b21a8' },
  Security:    { bg: '#f1f5f9', border: '#cbd5e1', text: '#334155' },
};

const SCHEMA_SECTIONS = [
  { id: 'tables',        label: 'Table Definitions', icon: '🗄️' },
  { id: 'relationships', label: 'Relationships',      icon: '🔗' },
  { id: 'semantic',      label: 'Semantic Map',       icon: '🔤' },
  { id: 'aliases',       label: 'Column Aliases',     icon: '🏷️' },
  { id: 'upload',        label: 'Upload Schema',      icon: '📤' },
];

function SchemaTab() {
  const [section, setSection]     = useState('tables');
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const importInputRef            = React.useRef();

  async function handleExport() {
    setExporting(true);
    try {
      const res  = await adminAPI.exportSchema();
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `schema_export_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setExporting(false);
    }
  }

  async function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true); setImportMsg('');
    try {
      const res = await adminAPI.importSchema(file);
      setImportMsg(`✅ ${res.data.message}`);
      setTimeout(() => setImportMsg(''), 4000);
    } catch (err) {
      setImportMsg('❌ ' + (err.response?.data?.error || err.message));
      setTimeout(() => setImportMsg(''), 5000);
    } finally {
      setImporting(false);
    }
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ ...S.sectionHeader, marginBottom: 20 }}>
        <div>
          <div style={S.sectionTitle}>Schema & Semantic Management</div>
          <div style={S.sectionSub}>Edit tables, business term mappings, or upload a schema HTML doc to auto-configure</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {importMsg && (
            <span style={{ fontSize: 13, fontWeight: 600, color: importMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>
              {importMsg}
            </span>
          )}
          <input ref={importInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleImport} />
          <button onClick={() => importInputRef.current.click()} disabled={importing} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 18px', borderRadius: 9, border: '1.5px solid #1B5E3B',
            background: '#fff', color: '#1B5E3B',
            fontWeight: 600, fontSize: 13, cursor: importing ? 'not-allowed' : 'pointer',
          }}>
            {importing ? '⏳ Importing…' : '⬆ Import Schema'}
          </button>
          <button onClick={handleExport} disabled={exporting} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 18px', borderRadius: 9, border: '1.5px solid #1B5E3B',
            background: exporting ? '#E8F6EE' : '#1B5E3B', color: exporting ? '#1B5E3B' : '#fff',
            fontWeight: 600, fontSize: 13, cursor: exporting ? 'not-allowed' : 'pointer',
          }}>
            {exporting ? '⏳ Exporting…' : '⬇ Export Schema'}
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
        {SCHEMA_SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)} style={{
            padding: '8px 18px', borderRadius: 9, border: '1.5px solid',
            borderColor: section === s.id ? '#1B5E3B' : '#e8e6f0',
            background: section === s.id ? '#E8F6EE' : '#fff',
            color: section === s.id ? '#1B5E3B' : '#6b7280',
            fontWeight: section === s.id ? 700 : 500, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>{s.icon} {s.label}</button>
        ))}
      </div>

      {section === 'tables'        && <TableDefsEditor />}
      {section === 'relationships' && <RelationshipsView />}
      {section === 'semantic'      && <SemanticEditor />}
      {section === 'aliases'       && <ColumnAliasesEditor />}
      {section === 'upload'        && <SchemaUpload onCommit={() => setSection('semantic')} />}
    </div>
  );
}

// ── Table Definitions Editor (DB-backed) ─────────────────────────────────

function TableDefsEditor() {
  const [tables,    setTables]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [modFilter, setModFilter] = useState('All');
  const [open,      setOpen]      = useState(null);
  const [adding,    setAdding]    = useState(false);
  const [newName,   setNewName]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await adminAPI.tableDefs.list(); setTables(r.data.tables); }
    catch { setError('Failed to load table definitions.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const modules = ['All', ...Array.from(new Set(tables.map(t => t.module).filter(Boolean)))];
  const filtered = modFilter === 'All' ? tables : tables.filter(t => t.module === modFilter);

  const handleAdd = async () => {
    const name = newName.trim().toUpperCase();
    if (!name) return;
    try {
      await adminAPI.tableDefs.create({ table_name: name });
      setNewName(''); setAdding(false);
      await load();
      setOpen(name);
    } catch (err) { setError(err.response?.data?.error || 'Failed to create table.'); }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete table definition "${name}"?`)) return;
    try {
      await adminAPI.tableDefs.delete(name);
      if (open === name) setOpen(null);
      setTables(t => t.filter(x => x.table_name !== name));
    } catch { setError('Failed to delete.'); }
  };

  const handleSaved = (name, updated) =>
    setTables(t => t.map(x => x.table_name === name ? { ...x, ...updated } : x));

  if (loading) return <div style={S.center}><Spinner /></div>;

  return (
    <div>
      {error && <div style={S.errorBanner}>{error}</div>}

      {/* Module filter pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {modules.map(m => (
          <button key={m} onClick={() => setModFilter(m)} style={{
            padding: '4px 12px', borderRadius: 20, border: '1.5px solid',
            borderColor: modFilter === m ? '#1B5E3B' : '#e8e6f0',
            background: modFilter === m ? '#E8F6EE' : '#fff',
            color: modFilter === m ? '#1B5E3B' : '#6b7280',
            fontSize: 12, fontWeight: modFilter === m ? 700 : 500, cursor: 'pointer',
          }}>{m}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9b9b9b' }}>{filtered.length} tables</span>
      </div>

      {/* Add table */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {adding ? (
          <>
            <input autoFocus style={{ ...S.finput, flex: 1, textTransform: 'uppercase' }}
              value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()} placeholder="Table name e.g. OSQ" />
            <button onClick={handleAdd} style={{ ...S.saveBtn, padding: '8px 16px', fontSize: 13 }}>Add</button>
            <button onClick={() => { setAdding(false); setNewName(''); }} style={{ ...S.actBtn, fontSize: 13 }}>Cancel</button>
          </>
        ) : (
          <button onClick={() => setAdding(true)} style={{ ...S.saveBtn, padding: '8px 16px', fontSize: 13 }}>+ Add Table</button>
        )}
      </div>

      {/* Table list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map(t => (
          <TableCard key={t.table_name} summary={t}
            isOpen={open === t.table_name}
            onToggle={() => setOpen(o => o === t.table_name ? null : t.table_name)}
            onDelete={() => handleDelete(t.table_name)}
            onSaved={updated => handleSaved(t.table_name, updated)}
          />
        ))}
      </div>
    </div>
  );
}

function buildLLMPreview(tableName, form) {
  if (!form) return '';
  const lines = ['-- ERP DATABASE SCHEMA (SQL Server / SAP B1)', '-- Only use tables and columns listed below.', ''];
  lines.push(`-- ${tableName}: ${form.description || ''}`);
  lines.push(`-- Table: [dbo].[${tableName}]`);
  for (const [col, desc] of Object.entries(form.columns || {}))
    lines.push(`--   ${col}: ${desc}`);
  if (form.changelog_hint) lines.push(`-- CHANGELOG RULE: ${form.changelog_hint}`);
  if (form.query_hint)     lines.push(`-- QUERY HINT: ${form.query_hint}`);
  if (form.join_hint)      lines.push(`-- JOIN HINT: ${form.join_hint}`);
  return lines.join('\n');
}

function TableCard({ summary, isOpen, onToggle, onDelete, onSaved }) {
  const [form,        setForm]        = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [pulling,     setPulling]     = useState(false);
  const [error,       setError]       = useState('');
  const [newCol,      setNewCol]      = useState({ name: '', def: '' });
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!isOpen || form) return;
    adminAPI.tableDefs.get(summary.table_name)
      .then(r => setForm(r.data.table))
      .catch(() => setError('Failed to load table detail.'));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const set    = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setCol = (col, val) => setForm(f => ({ ...f, columns: { ...f.columns, [col]: val } }));
  const addCol = () => {
    if (!newCol.name.trim()) return;
    setCol(newCol.name.trim(), newCol.def);
    setNewCol({ name: '', def: '' });
  };
  const removeCol = col => setForm(f => { const c = { ...f.columns }; delete c[col]; return { ...f, columns: c }; });

  const pullFromDB = async () => {
    setPulling(true);
    try {
      const r = await adminAPI.tableDefs.columnsPreview(summary.table_name);
      const merged = { ...(form.columns || {}) };
      r.data.columns.forEach(c => {
        if (!merged[c.COLUMN_NAME])
          merged[c.COLUMN_NAME] = `${c.DATA_TYPE}${c.CHARACTER_MAXIMUM_LENGTH ? `(${c.CHARACTER_MAXIMUM_LENGTH})` : ''} — `;
      });
      setForm(f => ({ ...f, columns: merged }));
    } catch { setError('Failed to pull columns from DB.'); }
    finally { setPulling(false); }
  };

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const payload = {
        module: form.module, table_type: form.table_type, description: form.description,
        columns: form.columns, composite_key: form.composite_key, is_changelog: form.is_changelog,
        parent_table: form.parent_table, join_table: form.join_table, join_key: form.join_key,
        changelog_hint: form.changelog_hint, query_hint: form.query_hint, join_hint: form.join_hint,
      };
      await adminAPI.tableDefs.update(summary.table_name, payload);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
      onSaved(payload);
    } catch { setError('Save failed.'); }
    finally { setSaving(false); }
  };

  const mc = MODULE_COLORS[summary.module] || { bg: '#f9fafb', border: '#e5e7eb', text: '#374151' };

  return (
    <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
      {/* Summary row */}
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#1B5E3B', fontFamily: 'monospace', minWidth: 80 }}>{summary.table_name}</span>
        {summary.module && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: mc.bg, border: `1px solid ${mc.border}`, color: mc.text }}>{summary.module}</span>
        )}
        {summary.table_type && (
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: '#f9fafb', border: '1px solid #e5e7eb', color: '#6b7280' }}>{summary.table_type}</span>
        )}
        {summary.is_changelog ? (
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>Changelog</span>
        ) : null}
        {summary.composite_key && (
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontFamily: 'monospace' }}>{summary.composite_key}</span>
        )}
        <span style={{ flex: 1, fontSize: 11, color: '#9b9b9b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary.description || '—'}</span>
        <span style={{ fontSize: 12, color: '#9b9b9b' }}>{isOpen ? '▲' : '▼'}</span>
        <button onClick={e => { e.stopPropagation(); onDelete(); }}
          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16, padding: '0 0 0 8px' }}>×</button>
      </div>

      {isOpen && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f0edf8' }}>
          {!form ? (
            <div style={{ ...S.center, padding: 20 }}><Spinner /></div>
          ) : (
            <>
              {error && <div style={{ ...S.errorBanner, marginTop: 12 }}>{error}</div>}

              {/* Description */}
              <div style={{ ...S.field, marginTop: 14, marginBottom: 12 }}>
                <div style={S.flabel}>Description</div>
                <input style={S.finput} value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="What this table stores" />
              </div>

              {/* Module / Table Type / Composite Key */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div style={S.field}>
                  <div style={{ ...S.flabel, display: 'flex', alignItems: 'center' }}>
                    Module
                    <InfoTooltip text={"Which business area this table belongs to.\n\nExamples: Sales, Purchase, Inventory, Master\n\nUsed to filter & group tables in this admin panel. Has no effect on AI query generation — it's purely for your organisation."} />
                  </div>
                  <select style={S.finput} value={form.module || ''} onChange={e => set('module', e.target.value)}>
                    <option value="">— None —</option>
                    {['Sales','Purchase','Production','Jobwork','Accounts','Inventory','QC','Maintenance','Master','Security'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div style={S.field}>
                  <div style={{ ...S.flabel, display: 'flex', alignItems: 'center' }}>
                    Table Type
                    <InfoTooltip text={"The structural role of this table in the ERP data model:\n\n• Header — One row per document (e.g. OSQ = quotation header). Contains document-level info.\n\n• Line — Multiple rows per document (e.g. SQ1 = quotation line items). Set its Parent Table too.\n\n• Master — Reference/lookup data (e.g. OCUST = customers). No documents, just records.\n\nThe AI uses this to understand how tables relate and generate correct JOINs."} />
                  </div>
                  <select style={S.finput} value={form.table_type || ''} onChange={e => set('table_type', e.target.value)}>
                    <option value="">— None —</option>
                    <option value="Header">Header</option>
                    <option value="Line">Line</option>
                    <option value="Master">Master</option>
                  </select>
                </div>
                <div style={S.field}>
                  <div style={{ ...S.flabel, display: 'flex', alignItems: 'center' }}>
                    Composite Key
                    <InfoTooltip text={"The set of columns that together uniquely identify a single record.\n\nFormat: ColumnA+ColumnB+ColumnC\n\nExample: DocNo+DocYear+Doc\n→ DocNo alone isn't unique because it resets every financial year. DocYear distinguishes the year, Doc the series.\n\nWhy it matters: Required for the Changelog filter to work correctly. A wrong key returns duplicate/stale rows and inflates values."} />
                  </div>
                  <input style={S.finput} value={form.composite_key || ''} onChange={e => set('composite_key', e.target.value)} placeholder="DocNo+DocYear+Doc" />
                </div>
              </div>

              {/* Changelog / Parent / Join Table / Join Key */}
              <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div style={S.field}>
                  <div style={{ ...S.flabel, display: 'flex', alignItems: 'center' }}>
                    Changelog
                    <InfoTooltip text={"Check this if the table uses insert-on-update versioning.\n\nInstead of editing a row, the ERP inserts a new row with changelog + 1. MAX(changelog) per document = the current/live record.\n\nWhy it matters: Without this filter, queries include all old versions of a record — inflating row counts by ~25% and total values by ~25%.\n\nHow to enable: Tick Yes, then write the correct SQL subquery in the Changelog Hint textarea below."} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.is_changelog} onChange={e => set('is_changelog', e.target.checked)} />
                    <span style={{ fontSize: 12, color: '#374151' }}>Yes</span>
                  </label>
                </div>
                <div style={S.field}>
                  <div style={{ ...S.flabel, display: 'flex', alignItems: 'center' }}>
                    Parent Table
                    <InfoTooltip text={"For Line tables only — enter the Header table this line table belongs to.\n\nExample: SO1 (Sales Order Lines) → parent is OSO (Sales Order Header).\n\nWhy it matters: Links line items to their parent document in the Relationships view. Also helps the AI know which header table to JOIN when querying line-level data.\n\nLeave empty for Header and Master tables."} />
                  </div>
                  <input style={S.finput} value={form.parent_table || ''} onChange={e => set('parent_table', e.target.value)} placeholder="e.g. OSO" />
                </div>
                <div style={S.field}>
                  <div style={{ ...S.flabel, display: 'flex', alignItems: 'center' }}>
                    Join Table (lines)
                    <InfoTooltip text={"For Header tables only — the line table(s) that contain detail rows for this document.\n\nExample: OSO (Sales Order Header) → SO1 (Sales Order Lines)\n\nMultiple tables: Enter comma-separated names, e.g. SO1,SO2\n\nNote: This is metadata for the Relationships view only. To instruct the AI on how to write the actual JOIN SQL, use the Join Hint textarea below.\n\nLeave empty for Line and Master tables."} />
                  </div>
                  <input style={S.finput} value={form.join_table || ''} onChange={e => set('join_table', e.target.value)} placeholder="e.g. SO1" />
                </div>
                <div style={S.field}>
                  <div style={{ ...S.flabel, display: 'flex', alignItems: 'center' }}>
                    Join Key
                    <InfoTooltip text={"The column(s) used to JOIN this table with its related tables (header ↔ lines).\n\nFormat: ColumnA+ColumnB+ColumnC\n\nExample: DocNo+DocYear+Doc\n→ Both the header and line table share these columns; they are used as the JOIN condition.\n\nWhy it matters: Reference info for the AI when generating JOIN clauses. Usually matches the Composite Key.\n\nFor the actual SQL JOIN syntax, write it in the Join Hint textarea below."} />
                  </div>
                  <input style={S.finput} value={form.join_key || ''} onChange={e => set('join_key', e.target.value)} placeholder="DocNo+DocYear+Doc" />
                </div>
              </div>

              {/* Columns */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={S.flabel}>Columns</div>
                <button onClick={pullFromDB} disabled={pulling} style={{ ...S.actBtn, fontSize: 11, padding: '4px 10px' }}>
                  {pulling ? <Spinner small /> : '⬇'} Pull from DB
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                {Object.entries(form.columns || {}).map(([col, def]) => (
                  <div key={col} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#1B5E3B', minWidth: 140, fontWeight: 600 }}>{col}</span>
                    <input style={{ ...S.finput, flex: 1, fontSize: 12 }} value={def} onChange={e => setCol(col, e.target.value)} />
                    <button onClick={() => removeCol(col)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                <input style={{ ...S.finput, width: 130, fontSize: 12 }} value={newCol.name}
                  onChange={e => setNewCol(c => ({ ...c, name: e.target.value }))} placeholder="Column name" />
                <input style={{ ...S.finput, flex: 1, fontSize: 12 }} value={newCol.def}
                  onChange={e => setNewCol(c => ({ ...c, def: e.target.value }))} placeholder="nvarchar - Description" />
                <button onClick={addCol} style={{ ...S.actBtn, fontSize: 12 }}>+ Col</button>
              </div>

              {/* Hints */}
              {[
                ['changelog_hint', 'Changelog Hint (SQL)', "SQL subquery injected into the AI prompt to filter only the latest version of each record.\n\nThis is sent to GPT verbatim as:\n-- CHANGELOG RULE: <your text>\n\nExample:\nWHERE t.[ChangeLog] = (SELECT MAX(t2.[ChangeLog]) FROM [dbo].[OSQ] t2 WHERE t2.[DocNo]=t.[DocNo] AND t2.[DocYear]=t.[DocYear] AND t2.[Doc]=t.[Doc])\n\nRequired when Changelog is checked. Without it, the AI won't know how to filter stale rows."],
                ['query_hint',     'Query Hint',           "General instructions for the AI about how to query this table.\n\nThis is sent to GPT verbatim as:\n-- QUERY HINT: <your text>\n\nExamples:\n• Always filter by DocYear = '25-26' unless user specifies a different year.\n• QtnStage values: Active, Closed, Lost, Cancelled.\n• Amounts are stored in Rupees, no conversion needed.\n\nUse this for business rules the AI wouldn't know from the schema alone."],
                ['join_hint',      'Join Hint',            "SQL JOIN instructions for the AI when this table needs to be joined with another.\n\nThis is sent to GPT verbatim as:\n-- JOIN HINT: <your text>\n\nExample (for OSO joining SO1):\nJOIN [dbo].[SO1] s ON s.DocNo=o.DocNo AND s.DocYear=o.DocYear AND s.Doc=o.Doc\n\nFor multiple line tables, add multiple JOIN lines here.\n\nThis is the correct place to define how joins work — the Join Table (lines) field above is metadata only."],
              ].map(([key, label, helpText]) => (
                <div key={key} style={{ ...S.field, marginBottom: 10 }}>
                  <div style={{ ...S.flabel, display: 'flex', alignItems: 'center' }}>
                    {label}
                    <InfoTooltip text={helpText} />
                  </div>
                  <textarea rows={3} style={{ ...S.finput, resize: 'vertical', fontSize: 12, lineHeight: 1.5 }}
                    value={form[key] || ''} onChange={e => set(key, e.target.value)} placeholder={`Optional — ${label}`} />
                </div>
              ))}

              {/* LLM Context Preview */}
              <div style={{ marginBottom: 12 }}>
                <button onClick={() => setShowPreview(p => !p)} style={{ ...S.actBtn, fontSize: 12, marginBottom: showPreview ? 8 : 0 }}>
                  {showPreview ? '▲ Hide' : '▼ Preview'} LLM Context
                </button>
                {showPreview && (
                  <pre style={{
                    background: '#0d0d1c', color: '#a5f3a5', fontSize: 11,
                    borderRadius: 8, padding: '12px 14px', overflowX: 'auto',
                    lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    border: '1px solid #1e1e32',
                  }}>
                    {buildLLMPreview(summary.table_name, form)}
                  </pre>
                )}
              </div>

              {/* Save */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                {saved && <span style={{ color: '#059669', fontSize: 13, alignSelf: 'center' }}>Saved ✓</span>}
                <button onClick={save} disabled={saving} style={{ ...S.saveBtn, padding: '8px 20px', fontSize: 13 }}>
                  {saving ? <><Spinner small /> Saving…</> : 'Save'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Relationships View ────────────────────────────────────────────────────

function RelationshipsView() {
  const [tables,  setTables]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('All');

  useEffect(() => {
    adminAPI.tableDefs.list()
      .then(r => setTables(r.data.tables))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={S.center}><Spinner /></div>;

  const modules = ['All', ...Array.from(new Set(tables.map(t => t.module).filter(Boolean)))];
  const visible = filter === 'All' ? tables : tables.filter(t => t.module === filter);

  // Build header→lines map
  const headers = visible.filter(t => t.table_type === 'Header');
  // Build line→parent map
  const lines   = visible.filter(t => t.table_type === 'Line' && t.parent_table);
  // Collect all unique cross-table refs from join_table
  const relMap = {};
  headers.forEach(h => { if (h.join_table) relMap[h.table_name] = h.join_table; });

  return (
    <div>
      {/* Module filter */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18, alignItems: 'center' }}>
        {modules.map(m => (
          <button key={m} onClick={() => setFilter(m)} style={{
            padding: '4px 12px', borderRadius: 20, border: '1.5px solid',
            borderColor: filter === m ? '#1B5E3B' : '#e8e6f0',
            background: filter === m ? '#E8F6EE' : '#fff',
            color: filter === m ? '#1B5E3B' : '#6b7280',
            fontSize: 12, fontWeight: filter === m ? 700 : 500, cursor: 'pointer',
          }}>{m}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Header → Line relationships */}
        <div>
          <div style={{ ...S.flabel, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Header → Line Tables</div>
          {headers.length === 0 && <div style={{ color: '#9b9b9b', fontSize: 13 }}>No header tables in this module.</div>}
          {headers.map(h => {
            const mc = MODULE_COLORS[h.module] || { bg: '#f9fafb', border: '#e5e7eb', text: '#374151' };
            const lineChildren = lines.filter(l => l.parent_table === h.table_name);
            return (
              <div key={h.table_name} style={{ ...S.card, marginBottom: 8, padding: '10px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: lineChildren.length ? 8 : 0 }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#1B5E3B' }}>{h.table_name}</span>
                  {h.module && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: mc.bg, border: `1px solid ${mc.border}`, color: mc.text }}>{h.module}</span>}
                  {h.composite_key && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontFamily: 'monospace' }}>{h.composite_key}</span>}
                  {h.is_changelog && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>Changelog</span>}
                </div>
                {lineChildren.length > 0 && (
                  <div style={{ paddingLeft: 12, borderLeft: '2px solid #e8e6f0', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {lineChildren.map(l => (
                      <div key={l.table_name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                        <span style={{ color: '#9b9b9b', fontSize: 10 }}>└</span>
                        <span style={{ fontFamily: 'monospace', color: '#6b7280', fontWeight: 600 }}>{l.table_name}</span>
                        <span style={{ color: '#9b9b9b', fontSize: 11 }}>{l.description || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                {lineChildren.length === 0 && h.join_table && (
                  <div style={{ paddingLeft: 12, borderLeft: '2px solid #e8e6f0', fontSize: 12, color: '#9b9b9b' }}>
                    └ <span style={{ fontFamily: 'monospace', color: '#6b7280' }}>{h.join_table}</span>
                    <span style={{ fontSize: 10, marginLeft: 6, color: '#81C784' }}>(set parent_table on line tables to link)</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Composite key summary */}
        <div>
          <div style={{ ...S.flabel, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Composite Keys</div>
          {visible.filter(t => t.composite_key).length === 0 && (
            <div style={{ color: '#9b9b9b', fontSize: 13 }}>No composite keys defined.</div>
          )}
          {visible.filter(t => t.composite_key).map(t => {
            const mc = MODULE_COLORS[t.module] || { bg: '#f9fafb', border: '#e5e7eb', text: '#374151' };
            return (
              <div key={t.table_name} style={{ ...S.card, marginBottom: 6, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: '#1B5E3B', minWidth: 80 }}>{t.table_name}</span>
                {t.module && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: mc.bg, border: `1px solid ${mc.border}`, color: mc.text }}>{t.module}</span>}
                <span style={{ fontSize: 10, color: '#6b7280' }}>{t.table_type}</span>
                <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 11, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 4 }}>{t.composite_key}</span>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

// ── Semantic Map Editor ───────────────────────────────────────────────────

function SemanticEditor() {
  const [semantic,    setSemantic]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [generating,  setGenerating]  = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState('');
  const [genMsg,      setGenMsg]      = useState('');
  const [newTerm,     setNewTerm]     = useState({ term: '', table: '', description: '', intent_hint: 'lookup' });
  const [editTerm,    setEditTerm]    = useState(null);

  useEffect(() => {
    adminAPI.getSemantic().then(r => setSemantic(r.data.semantic)).catch(() => setError('Failed to load.')).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try { await adminAPI.updateSemantic(semantic); setSaved(true); setTimeout(() => setSaved(false), 3000); }
    catch { setError('Save failed.'); } finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!window.confirm(
      'This will replace ALL current terms with ones generated from the tables in the DB.\n\n' +
      'status_macros and product_codes will be cleared (date_macros kept).\n\nProceed?'
    )) return;
    setGenerating(true); setGenMsg(''); setError('');
    try {
      const r = await adminAPI.generateSemantic();
      setSemantic(r.data.semantic);
      setGenMsg(`Generated ${r.data.term_count} terms from ${r.data.table_count} tables. Review and save.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  const addTerm = () => {
    const term = newTerm.term.trim().toLowerCase();
    if (!term || !newTerm.table.trim()) return;
    setSemantic(s => ({ ...s, terms: { ...s.terms, [term]: { table: newTerm.table.trim(), description: newTerm.description, intent_hint: newTerm.intent_hint } } }));
    setNewTerm({ term: '', table: '', description: '', intent_hint: 'lookup' });
  };

  const removeTerm = (term) =>
    setSemantic(s => { const t = { ...s.terms }; delete t[term]; return { ...s, terms: t }; });

  if (loading) return <div style={S.center}><Spinner /></div>;
  if (!semantic) return <div style={S.errorBanner}>{error}</div>;

  const terms = semantic.terms || {};

  return (
    <div>
      <OnboardingBanner step={3} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <span style={{ fontSize: 13, color: '#6b7280' }}>{Object.keys(terms).length} business terms mapped</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {error  && <span style={{ color: '#dc2626', fontSize: 13 }}>{error}</span>}
          {saved  && <span style={{ color: '#059669', fontSize: 13 }}>Saved ✓</span>}
          <button onClick={handleGenerate} disabled={generating} style={{ ...S.actBtn, padding: '8px 16px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            {generating ? <><Spinner small /> Generating…</> : '⚡ Generate from Tables'}
          </button>
          <button onClick={save} disabled={saving} style={{ ...S.saveBtn, padding: '8px 18px', fontSize: 13 }}>
            {saving ? <Spinner small /> : '💾'} {saving ? 'Saving…' : 'Save All'}
          </button>
        </div>
      </div>

      {saved && (
        <div role="alert" style={{ padding: '14px 18px', borderRadius: 10, background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '1px solid #86efac', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🎉</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>Setup complete! The AI is ready to query your database.</div>
            <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>Go to Home and start asking questions in plain English.</div>
          </div>
        </div>
      )}
      {genMsg && (
        <div role="alert" style={{ ...S.successBanner, marginBottom: 14 }}>
          ⚡ {genMsg}
        </div>
      )}

      {/* Add term */}
      <div style={{ ...S.card, marginBottom: 14 }}>
        <div style={{ ...S.cardTitle, marginBottom: 12 }}>Add Business Term</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={S.field}>
            <div style={S.flabel}>Term (what users type)</div>
            <input style={{ ...S.finput, width: 160 }} value={newTerm.term}
              onChange={e => setNewTerm(t => ({ ...t, term: e.target.value }))} placeholder="e.g. invoice" />
          </div>
          <div style={S.field}>
            <div style={S.flabel}>Table</div>
            <input style={{ ...S.finput, width: 100 }} value={newTerm.table}
              onChange={e => setNewTerm(t => ({ ...t, table: e.target.value }))} placeholder="e.g. OSQ" />
          </div>
          <div style={S.field}>
            <div style={S.flabel}>Description</div>
            <input style={{ ...S.finput, width: 200 }} value={newTerm.description}
              onChange={e => setNewTerm(t => ({ ...t, description: e.target.value }))} placeholder="Sales Invoice Header" />
          </div>
          <div style={S.field}>
            <div style={S.flabel}>Intent hint</div>
            <select style={{ ...S.finput, width: 110 }} value={newTerm.intent_hint}
              onChange={e => setNewTerm(t => ({ ...t, intent_hint: e.target.value }))}>
              <option value="lookup">lookup</option>
              <option value="aggregation">aggregation</option>
              <option value="top_n">top_n</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button onClick={addTerm} style={{ ...S.saveBtn, padding: '9px 16px', fontSize: 13 }}>+ Add</button>
          </div>
        </div>
      </div>

      {/* Terms table */}
      <div style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #D1F0E0' }}>
                {['Business Term', 'Table', 'Description', 'Intent', ''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(terms).map(([term, meta]) => (
                <tr key={term} style={{ borderBottom: '1px solid #f4f2fa' }}>
                  {editTerm === term ? (
                    <>
                      <td style={{ padding: '6px 8px' }}><input style={{ ...S.finput, width: '100%', fontSize: 12 }} value={term} disabled /></td>
                      <td style={{ padding: '6px 8px' }}><input style={{ ...S.finput, fontSize: 12 }} value={meta.table}
                        onChange={e => setSemantic(s => ({ ...s, terms: { ...s.terms, [term]: { ...meta, table: e.target.value } } }))} /></td>
                      <td style={{ padding: '6px 8px' }}><input style={{ ...S.finput, fontSize: 12 }} value={meta.description || ''}
                        onChange={e => setSemantic(s => ({ ...s, terms: { ...s.terms, [term]: { ...meta, description: e.target.value } } }))} /></td>
                      <td style={{ padding: '6px 8px' }}>
                        <select style={{ ...S.finput, fontSize: 12 }} value={meta.intent_hint || 'lookup'}
                          onChange={e => setSemantic(s => ({ ...s, terms: { ...s.terms, [term]: { ...meta, intent_hint: e.target.value } } }))}>
                          <option value="lookup">lookup</option>
                          <option value="aggregation">aggregation</option>
                          <option value="top_n">top_n</option>
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}><button onClick={() => setEditTerm(null)} style={{ ...S.actBtn, fontSize: 12 }}>Done</button></td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1a1a2e' }}>{term}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: '#1B5E3B', fontWeight: 700 }}>{meta.table}</td>
                      <td style={{ padding: '8px 10px', color: '#6b7280' }}>{meta.description || '—'}</td>
                      <td style={{ padding: '8px 10px' }}><span style={{ ...S.valueChip, fontSize: 11 }}>{meta.intent_hint || 'lookup'}</span></td>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => setEditTerm(term)} style={{ ...S.actBtn, fontSize: 11 }}>Edit</button>
                          <button onClick={() => removeTerm(term)} style={{ ...S.actBtn, fontSize: 11, color: '#dc2626', borderColor: 'rgba(220,38,38,0.3)' }}>×</button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── HTML Schema Upload ────────────────────────────────────────────────────

// ── Column Aliases Editor ─────────────────────────────────────────────────

function ColumnAliasesEditor() {
  const [aliases,   setAliases]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [editRow,   setEditRow]   = useState(null);   // { raw_name, display_name, intent }
  const [newRow,    setNewRow]    = useState({ raw_name: '', display_name: '', intent: '' });
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getColumnAliases();
      setAliases(res.data.aliases || []);
    } catch { setError('Failed to load aliases.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (raw, display, intent) => {
    if (!display.trim()) return;
    setSaving(true);
    try {
      await adminAPI.upsertColumnAlias(raw, display, intent || null);
      await load();
      setEditRow(null);
    } catch { setError('Failed to save.'); }
    finally { setSaving(false); }
  };

  const del = async (raw) => {
    if (!window.confirm(`Delete alias for "${raw}"?`)) return;
    try {
      await adminAPI.deleteColumnAlias(raw);
      await load();
    } catch { setError('Failed to delete.'); }
  };

  const addNew = async () => {
    if (!newRow.raw_name.trim() || !newRow.display_name.trim()) return;
    await save(newRow.raw_name.trim(), newRow.display_name.trim(), newRow.intent);
    setNewRow({ raw_name: '', display_name: '', intent: '' });
  };

  if (loading) return <div style={S.center}><Spinner /><span style={{ color: '#9b9b9b', marginLeft: 12 }}>Loading…</span></div>;

  return (
    <div>
      <div style={{ ...S.sectionHeader, marginBottom: 16 }}>
        <div>
          <div style={S.sectionTitle}>Column Aliases</div>
          <div style={S.sectionSub}>AI-suggested and admin-defined friendly names for ERP column headers. Changes apply immediately to all query results.</div>
        </div>
      </div>
      {error && <div style={S.errorBanner}>{error}</div>}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#E8F6EE', borderBottom: '2px solid #E0F5EE' }}>
            {['Raw Column', 'Display Name', 'Intent', 'Source', 'Status', 'Actions'].map(h => (
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#4b4b4b', fontSize: 12 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {aliases.map((a, i) => (
            <tr key={a.raw_name} style={{ background: i % 2 === 0 ? '#fff' : '#f2faf8', borderBottom: '1px solid #e4f5ed' }}>
              <td style={{ padding: '9px 14px', fontFamily: 'monospace', color: '#0A3D1F', fontWeight: 600 }}>{a.raw_name}</td>
              <td style={{ padding: '9px 14px' }}>
                {editRow?.raw_name === a.raw_name ? (
                  <input
                    value={editRow.display_name}
                    onChange={e => setEditRow(r => ({ ...r, display_name: e.target.value }))}
                    style={{ border: '1.5px solid #1B5E3B', borderRadius: 6, padding: '4px 8px', fontSize: 13, width: 160 }}
                    autoFocus
                  />
                ) : <span style={{ fontWeight: 500 }}>{a.display_name}</span>}
              </td>
              <td style={{ padding: '9px 14px', color: '#6b7280', fontSize: 12 }}>{a.intent || '—'}</td>
              <td style={{ padding: '9px 14px' }}>
                <span style={{ background: a.ai_suggested ? '#f0fdf4' : '#E8F6EE', color: a.ai_suggested ? '#15803d' : '#1B5E3B', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                  {a.ai_suggested ? 'AI' : 'Manual'}
                </span>
              </td>
              <td style={{ padding: '9px 14px' }}>
                <span style={{ background: a.confirmed ? '#f0fdf4' : '#fffbeb', color: a.confirmed ? '#15803d' : '#d97706', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                  {a.confirmed ? 'Confirmed' : 'Pending'}
                </span>
              </td>
              <td style={{ padding: '9px 14px' }}>
                {editRow?.raw_name === a.raw_name ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => save(a.raw_name, editRow.display_name, editRow.intent)} disabled={saving} style={{ ...S.saveBtn, padding: '4px 12px', fontSize: 12 }}>Save</button>
                    <button onClick={() => setEditRow(null)} style={{ background: 'none', border: '1px solid #cce8d8', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditRow({ ...a })} style={{ background: 'none', border: '1px solid #C8EDD6', color: '#1B5E3B', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => del(a.raw_name)} style={{ background: 'none', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Delete</button>
                  </div>
                )}
              </td>
            </tr>
          ))}

          {/* Add new row */}
          <tr style={{ background: '#f9f8ff', borderTop: '2px solid #E0F5EE' }}>
            <td style={{ padding: '10px 14px' }}>
              <input value={newRow.raw_name} onChange={e => setNewRow(r => ({ ...r, raw_name: e.target.value }))} placeholder="RawColName" style={{ border: '1.5px solid #C8EDD6', borderRadius: 6, padding: '5px 8px', fontSize: 12, width: 120, fontFamily: 'monospace' }} />
            </td>
            <td style={{ padding: '10px 14px' }}>
              <input value={newRow.display_name} onChange={e => setNewRow(r => ({ ...r, display_name: e.target.value }))} placeholder="Friendly Name" style={{ border: '1.5px solid #C8EDD6', borderRadius: 6, padding: '5px 8px', fontSize: 12, width: 150 }} />
            </td>
            <td style={{ padding: '10px 14px' }}>
              <input value={newRow.intent} onChange={e => setNewRow(r => ({ ...r, intent: e.target.value }))} placeholder="optional" style={{ border: '1.5px solid #C8EDD6', borderRadius: 6, padding: '5px 8px', fontSize: 12, width: 100 }} />
            </td>
            <td colSpan={2} style={{ padding: '10px 14px', color: '#9b9b9b', fontSize: 12 }}>Manual entry</td>
            <td style={{ padding: '10px 14px' }}>
              <button onClick={addNew} disabled={!newRow.raw_name.trim() || !newRow.display_name.trim() || saving} style={{ ...S.saveBtn, padding: '5px 14px', fontSize: 12 }}>+ Add</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SchemaUpload({ onCommit }) {
  const [file,        setFile]        = useState(null);
  const [parsing,     setParsing]     = useState(false);
  const [preview,     setPreview]     = useState(null);  // { parsed_tables, parsed_semantic, table_count, new_terms }
  const [committing,  setCommitting]  = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  const handleFile = (e) => {
    setFile(e.target.files[0]);
    setPreview(null); setError(''); setSuccess('');
  };

  const parse = async () => {
    if (!file) return;
    setParsing(true); setError(''); setPreview(null);
    try {
      const res = await adminAPI.uploadSchema(file);
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Parse failed.');
    } finally { setParsing(false); }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true); setError('');
    try {
      const res = await adminAPI.commitSchema(preview.parsed_tables, preview.parsed_semantic);
      setSuccess(res.data.message);
      setPreview(null); setFile(null);
      setTimeout(onCommit, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Commit failed.');
    } finally { setCommitting(false); }
  };

  return (
    <div>
      <OnboardingBanner step={2} />
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ ...S.cardTitle, marginBottom: 8 }}>📤 Upload Schema HTML Document</div>
        <div style={{ ...S.cardSub, marginBottom: 16 }}>
          Upload your schema HTML file (must contain a <code>var modules = [...]</code> array with table definitions).
          The system will parse it and show a preview before committing.
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 9, border: '1.5px dashed #81C784', background: '#faf8ff', cursor: 'pointer', fontSize: 13, color: '#1B5E3B', fontWeight: 500 }}>
            📁 {file ? file.name : 'Choose HTML file'}
            <input type="file" accept=".html,.htm" onChange={handleFile} style={{ display: 'none' }} />
          </label>
          <button onClick={parse} disabled={!file || parsing} style={{ ...S.saveBtn, padding: '9px 20px', fontSize: 13, opacity: (!file || parsing) ? 0.5 : 1 }}>
            {parsing ? <><Spinner small /> Parsing…</> : '🔍 Parse'}
          </button>
        </div>
      </div>

      {error   && <div role="alert" style={{ ...S.errorBanner, marginBottom: 16 }}>{error}</div>}
      {success && (
        <div role="alert" style={{ marginBottom: 16 }}>
          <div style={{ ...S.successBanner, marginBottom: 10 }}>{success}</div>
          <div style={{ padding: '14px 18px', borderRadius: 10, background: 'linear-gradient(135deg, #E8F6EE, #D1F0E0)', border: '1px solid #81C784', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0A3D1F' }}>Schema saved! One step left.</div>
              <div style={{ fontSize: 12, color: '#1B5E3B', marginTop: 2 }}>Generate the semantic map so the AI can match plain-English questions to your tables.</div>
            </div>
            <button onClick={onCommit} style={{ ...S.saveBtn, padding: '8px 18px', fontSize: 13, whiteSpace: 'nowrap' }}>
              Generate Semantic Map →
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div style={S.card}>
          <div style={{ ...S.cardTitle, marginBottom: 16 }}>Preview — Ready to Commit</div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, background: '#E8F6EE', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#1B5E3B' }}>{preview.table_count}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Tables parsed</div>
            </div>
            <div style={{ flex: 1, background: '#f0fdf4', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#059669' }}>+{preview.new_terms}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>New semantic terms</div>
            </div>
          </div>

          <div style={{ ...S.flabel, marginBottom: 8 }}>Tables that will be added / replaced:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
            {Object.keys(preview.parsed_tables).map(t => (
              <span key={t} style={{ padding: '3px 10px', borderRadius: 6, background: '#D1F0E0', color: '#1B5E3B', fontSize: 12, fontWeight: 600, fontFamily: 'monospace' }}>{t}</span>
            ))}
          </div>

          {preview.auto_detected && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ ...S.flabel, marginBottom: 10 }}>Auto-detected from schema:</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: '📋 Changelog tables', items: preview.auto_detected.changelog_tables, color: '#9a3412', bg: '#fff7ed', border: '#fed7aa' },
                  { label: '🔑 Composite keys',   items: preview.auto_detected.composite_keys,   color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe' },
                  { label: '📄 Header tables',    items: preview.auto_detected.header_tables,    color: '#166534', bg: '#f0fdf4', border: '#bbf7d0' },
                  { label: '📝 Line tables',      items: preview.auto_detected.line_tables,      color: '#6b21a8', bg: '#f0fdf4', border: '#C8EDD6' },
                  { label: '🔗 Parent links',     items: preview.auto_detected.parent_links,     color: '#374151', bg: '#f9fafb', border: '#e5e7eb' },
                ].map(({ label, items, color, bg, border }) => items && items.length > 0 && (
                  <div key={label} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 6 }}>{label} ({items.length})</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {items.slice(0, 8).map(item => (
                        <span key={item} style={{ fontSize: 10, fontFamily: 'monospace', background: 'rgba(255,255,255,0.7)', border: `1px solid ${border}`, borderRadius: 4, padding: '1px 6px', color }}>{item}</span>
                      ))}
                      {items.length > 8 && <span style={{ fontSize: 10, color, opacity: 0.7 }}>+{items.length - 8} more</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={commit} disabled={committing} style={{ ...S.saveBtn, padding: '10px 24px' }}>
              {committing ? <><Spinner small /> Committing…</> : '✅ Commit Schema'}
            </button>
            <button onClick={() => setPreview(null)} style={S.cancelBtn}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Database Connection Tab ───────────────────────────────────────────────

function DatabaseTab({ onGoToSchema }) {
  const [cfg,      setCfg]      = useState({ host: '', port: 1433, database: '', user: '', password: '', encrypt: false });
  const [current,  setCurrent]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [testing,  setTesting]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [testMsg,  setTestMsg]  = useState(null);   // { ok, text }
  const [saveMsg,  setSaveMsg]  = useState(null);
  const [showPw,   setShowPw]   = useState(false);

  useEffect(() => {
    adminAPI.dbConfig.get()
      .then(r => {
        const c = r.data.config;
        setCurrent(c);
        setCfg(f => ({ ...f, host: c.host || '', port: c.port || 1433, database: c.database || '', user: c.user || '', encrypt: !!c.encrypt }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => { setCfg(f => ({ ...f, [k]: v })); setTestMsg(null); setSaveMsg(null); };

  const handleTest = async () => {
    setTesting(true); setTestMsg(null);
    try {
      const r = await adminAPI.dbConfig.test(cfg);
      setTestMsg({ ok: true, text: r.data.message });
    } catch (err) {
      setTestMsg({ ok: false, text: err.response?.data?.error || 'Connection failed.' });
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const r = await adminAPI.dbConfig.update(cfg);
      setSaveMsg({ ok: true, text: r.data.message });
      const ref = await adminAPI.dbConfig.get();
      setCurrent(ref.data.config);
    } catch (err) {
      setSaveMsg({ ok: false, text: err.response?.data?.error || 'Failed to switch connection.' });
    } finally { setSaving(false); }
  };

  if (loading) return <div style={S.center}><Spinner /></div>;

  return (
    <div style={{ maxWidth: 680 }}>
      <OnboardingBanner step={saveMsg?.ok ? 2 : 1} />
      <div style={{ ...S.sectionHeader, marginBottom: 24 }}>
        <div>
          <div style={S.sectionTitle}>Database Connection</div>
          <div style={S.sectionSub}>
            Configure which SQL Server the AI pipeline queries. Changes take effect immediately — no restart needed.
          </div>
        </div>
      </div>

      {/* Current connection status */}
      {current && (
        <div style={{ ...S.card, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#059669', flexShrink: 0, boxShadow: '0 0 0 3px rgba(5,150,105,0.2)' }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
              {current.database}
              <span style={{ fontWeight: 400, color: '#9b9b9b', marginLeft: 6 }}>@ {current.host}:{current.port}</span>
            </div>
            <div style={{ fontSize: 11, color: '#9b9b9b', marginTop: 2 }}>
              User: {current.user} · Source: <span style={{ color: current.source === 'override' ? '#1B5E3B' : '#059669', fontWeight: 600 }}>{current.source}</span>
            </div>
          </div>
        </div>
      )}

      <div style={S.card}>
        <div style={{ ...S.cardTitle, marginBottom: 18 }}>New Connection Settings</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginBottom: 12 }}>
          <div style={S.field}>
            <div style={S.flabel}>Host / Server</div>
            <input style={S.finput} value={cfg.host} onChange={e => set('host', e.target.value)} placeholder="192.168.1.10 or SERVERNAME" />
          </div>
          <div style={S.field}>
            <div style={S.flabel}>Port</div>
            <input style={S.finput} type="number" value={cfg.port} onChange={e => set('port', parseInt(e.target.value) || 1433)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div style={S.field}>
            <div style={S.flabel}>Database Name</div>
            <input style={S.finput} value={cfg.database} onChange={e => set('database', e.target.value)} placeholder="ERP_DB" />
          </div>
          <div style={S.field}>
            <div style={S.flabel}>Username</div>
            <input style={S.finput} value={cfg.user} onChange={e => set('user', e.target.value)} placeholder="sa" />
          </div>
        </div>

        <div style={{ ...S.field, marginBottom: 14 }}>
          <div style={S.flabel}>Password</div>
          <div style={S.keyRow}>
            <input
              type={showPw ? 'text' : 'password'}
              style={S.keyInput}
              value={cfg.password}
              onChange={e => set('password', e.target.value)}
              placeholder="••••••••"
            />
            <button type="button" onClick={() => setShowPw(s => !s)} style={S.eyeBtn}>
              {showPw
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9b9b9b" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9b9b9b" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer', fontSize: 13, color: '#374151' }}>
          <input type="checkbox" checked={cfg.encrypt} onChange={e => set('encrypt', e.target.checked)} />
          Encrypt connection (SSL)
        </label>

        {testMsg && (
          <div role="alert" style={{ ...( testMsg.ok ? S.successBanner : S.errorBanner ), marginBottom: 14 }}>
            {testMsg.ok ? '✓ ' : '✗ '}{testMsg.text}
          </div>
        )}
        {saveMsg && (
          <div role="alert" style={{ ...(saveMsg.ok ? S.successBanner : S.errorBanner), marginBottom: 14 }}>
            {saveMsg.ok ? '✓ ' : '✗ '}{saveMsg.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleTest} disabled={testing || !cfg.host || !cfg.database || !cfg.user || !cfg.password}
            style={{ ...S.actBtn, padding: '9px 20px', fontSize: 13, opacity: (testing || !cfg.host) ? 0.5 : 1 }}>
            {testing ? <><Spinner small /> Testing…</> : '🔌 Test Connection'}
          </button>
          <button onClick={handleSave} disabled={saving || !testMsg?.ok}
            style={{ ...S.saveBtn, opacity: (saving || !testMsg?.ok) ? 0.5 : 1 }}>
            {saving ? <><Spinner small /> Connecting…</> : '💾 Save & Connect'}
          </button>
        </div>
        {!testMsg?.ok && <div style={{ fontSize: 11, color: '#9b9b9b', marginTop: 8 }}>Run Test Connection first — the Save button unlocks once the connection succeeds.</div>}
        {saveMsg?.ok && onGoToSchema && (
          <div style={{ marginTop: 16, padding: '14px 18px', borderRadius: 10, background: 'linear-gradient(135deg, #E8F6EE, #D1F0E0)', border: '1px solid #81C784', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0A3D1F' }}>Connection live! Step 2 is next.</div>
              <div style={{ fontSize: 12, color: '#1B5E3B', marginTop: 2 }}>Upload your schema HTML so the AI knows your tables.</div>
            </div>
            <button onClick={onGoToSchema} style={{ ...S.saveBtn, padding: '8px 18px', fontSize: 13, whiteSpace: 'nowrap' }}>
              Upload Schema →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── AI Config Tab ─────────────────────────────────────────────────────────

const AI_SUB_TABS = [
  { id: 'profile',      label: 'Company Profile' },
  { id: 'modules',      label: 'Modules' },
  { id: 'dictionary',   label: 'Data Dictionary' },
  { id: 'playground',   label: 'Playground' },
  { id: 'integrations', label: '📡 Integrations' },
];

function AIConfigTab() {
  const [sub, setSub] = useState('profile');
  const subStyle = (id) => ({
    padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: sub === id ? 700 : 500,
    background: sub === id ? '#1B5E3B' : '#E8F6EE', color: sub === id ? '#fff' : '#1B5E3B', transition: 'all 0.15s',
  });
  return (
    <div style={{ maxWidth: 900 }}>
      <div style={S.sectionHeader}>
        <div>
          <div style={S.sectionTitle}>AI Configuration</div>
          <div style={S.sectionSub}>Company profile, modules, data dictionary, and query testing</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {AI_SUB_TABS.map(t => <button key={t.id} style={subStyle(t.id)} onClick={() => setSub(t.id)}>{t.label}</button>)}
      </div>
      {sub === 'profile'      && <CompanyProfileSection />}
      {sub === 'modules'      && <ModulesSection />}
      {sub === 'dictionary'   && <DataDictionarySection />}
      {sub === 'playground'   && <PlaygroundSection />}
      {sub === 'integrations' && <IntegrationsSection />}
    </div>
  );
}

// ── Company Profile ───────────────────────────────────────────────────────

function CompanyProfileSection() {
  const [desc, setDesc]   = useState('');
  const [ci, setCi]       = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  useEffect(() => {
    adminAPI.getCompanyProfile().then(r => { setDesc(r.data.description || ''); setCi(r.data.custom_instructions || ''); }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    try { await adminAPI.updateCompanyProfile({ description: desc, custom_instructions: ci }); setMsg('Saved!'); }
    catch { setMsg('Save failed.'); }
    setSaving(false);
  };

  if (loading) return <div style={S.center}><Spinner /></div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={S.card}>
        <div style={S.cardTitle}>Company Description</div>
        <div style={S.cardSub}>What your company does — helps the AI understand context, ERP type, fiscal year, currency, industry.</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, marginBottom: 10 }}>
          Example: <em>"Precision Engineering Works Pvt. Ltd. — manufacturing company in Gujarat. Uses custom ERP. Fiscal year: April–March. Currency: INR. Sells to enterprises like Tata Steel, L&T."</em>
        </div>
        <textarea
          value={desc} onChange={e => setDesc(e.target.value)} rows={5}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #D1F0E0', fontSize: 13, color: '#1a1a2e', resize: 'vertical', fontFamily: 'inherit', outline: 'none', background: '#F8FEFC', boxSizing: 'border-box' }}
          placeholder="Describe your company, ERP system, industry, fiscal year, currency..."
        />
      </div>
      <div style={S.card}>
        <div style={S.cardTitle}>Custom Instructions</div>
        <div style={S.cardSub}>Rules the AI should always follow for this company's queries.</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, marginBottom: 10 }}>
          Example: <em>"Always filter by the current financial year unless a specific year is mentioned. Never show cancelled records unless explicitly asked."</em>
        </div>
        <textarea
          value={ci} onChange={e => setCi(e.target.value)} rows={4}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #D1F0E0', fontSize: 13, color: '#1a1a2e', resize: 'vertical', fontFamily: 'inherit', outline: 'none', background: '#F8FEFC', boxSizing: 'border-box' }}
          placeholder="e.g. Always use fiscal year April-March. Default date range is current FY..."
        />
      </div>
      {msg && <div style={msg === 'Saved!' ? S.successBanner : S.errorBanner}>{msg}</div>}
      <button style={{ ...S.saveBtn, alignSelf: 'flex-start' }} onClick={save} disabled={saving}>
        {saving ? <Spinner small /> : '💾'} Save Company Profile
      </button>
    </div>
  );
}

// ── Modules ───────────────────────────────────────────────────────────────

function ModulesSection() {
  const [modules, setModules]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName]   = useState('');
  const [newDesc, setNewDesc]   = useState('');
  const [suggesting, setSuggesting] = useState(null);
  const [saving, setSaving]     = useState(null);
  const [msg, setMsg]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminAPI.modules.list();
    setModules(r.data.modules || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createMod = async () => {
    if (!newName.trim()) return;
    await adminAPI.modules.create({ name: newName.trim(), description: newDesc.trim() });
    setCreating(false); setNewName(''); setNewDesc('');
    load();
  };

  const updateField = (id, field, val) => {
    setModules(prev => prev.map(m => m.id === id ? { ...m, [field]: val } : m));
  };

  const saveModule = async (m) => {
    setSaving(m.id);
    await adminAPI.modules.update(m.id, { name: m.name, description: m.description, table_names: m.table_names, knowledge_text: m.knowledge_text, verified: m.verified });
    setSaving(null); setMsg('Saved!'); setTimeout(() => setMsg(''), 2000);
  };

  const deleteMod = async (id) => {
    if (!window.confirm('Delete this module?')) return;
    await adminAPI.modules.delete(id); load();
  };

  const suggestTables = async (m) => {
    setSuggesting(m.id);
    try {
      const r = await adminAPI.modules.suggestTables({ module_name: m.name, module_description: m.description });
      updateField(m.id, 'table_names', r.data.suggested_tables || []);
    } catch { /* ignore */ }
    setSuggesting(null);
  };

  const toggleTable = (m, tbl) => {
    const cur = m.table_names || [];
    updateField(m.id, 'table_names', cur.includes(tbl) ? cur.filter(t => t !== tbl) : [...cur, tbl]);
  };

  if (loading) return <div style={S.center}><Spinner /></div>;

  const inputSt = { padding: '8px 11px', borderRadius: 8, border: '1px solid #D1F0E0', fontSize: 13, color: '#1a1a2e', background: '#F8FEFC', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const taBtnSt = (active) => ({ padding: '3px 10px', borderRadius: 6, border: `1px solid ${active ? '#1B5E3B' : '#D1F0E0'}`, background: active ? '#1B5E3B' : '#F8FEFC', color: active ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 600, cursor: 'pointer', margin: '2px' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>Group your ERP tables into business modules with context for the AI.</div>
        <button style={{ ...S.saveBtn, padding: '8px 16px', fontSize: 12 }} onClick={() => setCreating(true)}>+ New Module</button>
      </div>

      {msg && <div style={S.successBanner}>{msg}</div>}

      {creating && (
        <div style={{ ...S.card, border: '2px solid #1B5E3B' }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <input style={{ ...inputSt, flex: 1 }} placeholder="Module name (e.g. Sales & Quotations)" value={newName} onChange={e => setNewName(e.target.value)} />
            <input style={{ ...inputSt, flex: 2 }} placeholder="Short description" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...S.saveBtn, padding: '7px 16px', fontSize: 12 }} onClick={createMod}>Create</button>
            <button style={S.cancelBtn} onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {modules.length === 0 && !creating && (
        <div style={{ ...S.card, textAlign: 'center', color: '#9b9b9b', padding: 40 }}>No modules yet. Create your first module to group related tables.</div>
      )}

      {modules.map(m => (
        <div key={m.id} style={{ ...S.card, border: expanded === m.id ? '2px solid #1B5E3B' : '1px solid #D1F0E0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{m.name}</span>
                {m.verified ? <span style={{ fontSize: 10, background: '#E8F6EE', color: '#1B5E3B', border: '1px solid #C8EDD6', borderRadius: 10, padding: '1px 8px', fontWeight: 700 }}>Verified</span>
                  : <span style={{ fontSize: 10, background: '#FFF3CD', color: '#856404', border: '1px solid #FFE69C', borderRadius: 10, padding: '1px 8px', fontWeight: 700 }}>Unverified</span>}
              </div>
              <div style={{ fontSize: 12, color: '#9b9b9b', marginTop: 2 }}>{m.description || 'No description'} · {(m.table_names || []).length} tables</div>
            </div>
            <button style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: '4px 8px' }} onClick={e => { e.stopPropagation(); deleteMod(m.id); }}>✕</button>
            <span style={{ color: '#9b9b9b', fontSize: 16 }}>{expanded === m.id ? '▲' : '▼'}</span>
          </div>

          {expanded === m.id && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 14, borderTop: '1px solid #E8F6EE' }}>
              <div style={S.field}>
                <label style={S.flabel}>Module Name</label>
                <input style={inputSt} value={m.name} onChange={e => updateField(m.id, 'name', e.target.value)} />
              </div>
              <div style={S.field}>
                <label style={S.flabel}>Description</label>
                <input style={inputSt} value={m.description || ''} onChange={e => updateField(m.id, 'description', e.target.value)} placeholder="What business area does this module cover?" />
              </div>
              <div style={S.field}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label style={S.flabel}>Tables assigned ({(m.table_names || []).length})</label>
                  <button style={{ ...S.actBtn, display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => suggestTables(m)} disabled={!!suggesting}>
                    {suggesting === m.id ? <Spinner small /> : '✨'} Suggest Tables
                  </button>
                </div>
                <ModuleTablePicker selected={m.table_names || []} onToggle={(tbl) => toggleTable(m, tbl)} />
              </div>
              <div style={S.field}>
                <label style={S.flabel}>Module Knowledge <span style={{ color: '#9b9b9b', fontWeight: 400 }}>(business rules for the AI)</span></label>
                <textarea value={m.knowledge_text || ''} onChange={e => updateField(m.id, 'knowledge_text', e.target.value)} rows={3}
                  style={{ ...inputSt, resize: 'vertical' }}
                  placeholder="e.g. 'Revenue = net of refunds. Active quotations have QtnStage = Active. Fiscal year starts April 1.'" />
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button style={{ ...S.saveBtn, padding: '8px 18px', fontSize: 13 }} onClick={() => saveModule(m)} disabled={saving === m.id}>
                  {saving === m.id ? <Spinner small /> : '💾'} Save
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#1B5E3B', fontWeight: 600 }}>
                  <input type="checkbox" checked={!!m.verified} onChange={e => updateField(m.id, 'verified', e.target.checked)} style={{ accentColor: '#1B5E3B' }} />
                  Mark as Verified
                </label>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ModuleTablePicker({ selected, onToggle }) {
  const [tables, setTables] = useState([]);
  useEffect(() => { adminAPI.tableDefs.list().then(r => setTables((r.data.tables || []).map(t => t.table_name || t.name).filter(Boolean))); }, []);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px 10px', background: '#F8FEFC', borderRadius: 8, border: '1px solid #D1F0E0', maxHeight: 140, overflowY: 'auto' }}>
      {tables.length === 0 && <span style={{ fontSize: 12, color: '#9b9b9b' }}>No tables in TableDefinitions yet.</span>}
      {tables.map(tbl => (
        <button key={tbl} onClick={() => onToggle(tbl)}
          style={{ padding: '3px 10px', borderRadius: 6, border: `1px solid ${selected.includes(tbl) ? '#1B5E3B' : '#D1F0E0'}`, background: selected.includes(tbl) ? '#1B5E3B' : '#F8FEFC', color: selected.includes(tbl) ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
          {tbl}
        </button>
      ))}
    </div>
  );
}

// ── Data Dictionary ───────────────────────────────────────────────────────

function DataDictionarySection() {
  const [enums, setEnums]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [discovering, setDisc]    = useState(false);
  const [discovered, setDisc2]    = useState([]);
  const [expanded, setExpanded]   = useState({});
  const [newVal, setNewVal]       = useState({});
  const [newMng, setNewMng]       = useState({});
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminAPI.columnEnums.list();
    setEnums(r.data.enums || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = enums.reduce((acc, e) => {
    if (!acc[e.table_name]) acc[e.table_name] = {};
    if (!acc[e.table_name][e.column_name]) acc[e.table_name][e.column_name] = [];
    acc[e.table_name][e.column_name].push(e);
    return acc;
  }, {});

  const discover = async () => {
    setDisc(true);
    try { const r = await adminAPI.columnEnums.discover(); setDisc2(r.data.discovered || []); }
    catch { /* ignore */ }
    setDisc(false);
  };

  const addEnum = async (table_name, column_name) => {
    const key = `${table_name}.${column_name}`;
    const val = (newVal[key] || '').trim();
    if (!val) return;
    setSaving(true);
    await adminAPI.columnEnums.upsert({ table_name, column_name, value: val, meaning: (newMng[key] || '').trim(), value_type: 'fixed' });
    setNewVal(p => ({ ...p, [key]: '' })); setNewMng(p => ({ ...p, [key]: '' }));
    setSaving(false); load();
  };

  const deleteEnum = async (id) => { await adminAPI.columnEnums.delete(id); load(); };

  const addFromDiscovered = async (item) => {
    setSaving(true);
    for (const v of item.values) {
      await adminAPI.columnEnums.upsert({ table_name: item.table_name, column_name: item.column_name, value: v, meaning: '', value_type: 'fixed' });
    }
    setSaving(false); setMsg(`Added ${item.values.length} values for ${item.column_name}`); load(); setTimeout(() => setMsg(''), 3000);
  };

  const inputSt = { padding: '7px 10px', borderRadius: 8, border: '1px solid #D1F0E0', fontSize: 12, color: '#1a1a2e', background: '#F8FEFC', outline: 'none' };

  if (loading) return <div style={S.center}><Spinner /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>Define what each column value means — the AI uses this to write accurate filters.</div>
        <button style={{ ...S.saveBtn, padding: '8px 16px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }} onClick={discover} disabled={discovering}>
          {discovering ? <Spinner small /> : '🔍'} Discover Columns
        </button>
      </div>

      {msg && <div style={S.successBanner}>{msg}</div>}

      {discovered.length > 0 && (
        <div style={S.card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#1a1a2e' }}>Discovered Columns (click to import)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {discovered.map((d, i) => (
              <button key={i} onClick={() => addFromDiscovered(d)} disabled={saving}
                style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #D1F0E0', background: '#F8FEFC', fontSize: 12, cursor: 'pointer', color: '#1B5E3B', fontWeight: 600 }}>
                {d.table_name}.{d.column_name} ({d.values.slice(0,3).join(', ')}{d.values.length > 3 ? '…' : ''})
              </button>
            ))}
          </div>
        </div>
      )}

      {Object.keys(grouped).length === 0 && discovered.length === 0 && (
        <div style={{ ...S.card, textAlign: 'center', color: '#9b9b9b', padding: 40 }}>
          No enum definitions yet. Click "Discover Columns" to auto-detect status/type columns from your DB.
        </div>
      )}

      {Object.entries(grouped).map(([tbl, cols]) => (
        <div key={tbl} style={S.card}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            {tbl} <span style={{ fontSize: 11, color: '#9b9b9b', fontWeight: 400 }}>({Object.keys(cols).length} columns)</span>
          </div>
          {Object.entries(cols).map(([col, vals]) => {
            const key = `${tbl}.${col}`;
            const open = expanded[key];
            return (
              <div key={col} style={{ marginBottom: 10, borderRadius: 8, border: '1px solid #E8F6EE', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#F8FEFC', cursor: 'pointer', gap: 8 }}
                  onClick={() => setExpanded(p => ({ ...p, [key]: !open }))}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#1B5E3B', flex: 1 }}>{col}</span>
                  <span style={{ fontSize: 11, color: '#9b9b9b' }}>{vals.length} values</span>
                  <span style={{ color: '#9b9b9b' }}>{open ? '▲' : '▼'}</span>
                </div>
                {open && (
                  <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {vals.map(v => (
                      <div key={v.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1B5E3B', background: '#E8F6EE', padding: '2px 8px', borderRadius: 5, minWidth: 60, textAlign: 'center' }}>{v.value}</span>
                        <span style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>{v.meaning || <em style={{ color: '#ccc' }}>no meaning set</em>}</span>
                        <button style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }} onClick={() => deleteEnum(v.id)}>✕</button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <input style={{ ...inputSt, flex: '0 0 100px' }} placeholder="Value" value={newVal[key] || ''} onChange={e => setNewVal(p => ({ ...p, [key]: e.target.value }))} />
                      <input style={{ ...inputSt, flex: 1 }} placeholder="Meaning (e.g. Open quotation)" value={newMng[key] || ''} onChange={e => setNewMng(p => ({ ...p, [key]: e.target.value }))} />
                      <button style={{ ...S.actBtn, fontSize: 12, padding: '5px 12px' }} onClick={() => addEnum(tbl, col)}>+ Add</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ ...S.card, border: '1.5px dashed #C8EDD6' }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', marginBottom: 8 }}>Add manually</div>
        <AddEnumForm onSave={() => load()} />
      </div>
    </div>
  );
}

function ThemedSelect({ value, onChange, options, placeholder = 'Select…', minWidth = 160 }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find(o => o === value);
  return (
    <div ref={ref} style={{ position: 'relative', minWidth }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: '100%', minWidth, height: 34, padding: '0 10px', borderRadius: 8, border: `1px solid ${open ? '#1B5E3B' : '#D1F0E0'}`, background: '#F8FEFC', fontSize: 12, color: value ? '#1a1a2e' : '#9b9b9b', cursor: 'pointer', outline: 'none', boxShadow: open ? '0 0 0 2px #C8EDD6' : 'none', transition: 'border 0.15s, box-shadow 0.15s' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected || placeholder}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={open ? '#1B5E3B' : '#9b9b9b'} strokeWidth="2.5" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 999, marginTop: 4, minWidth: '100%', maxHeight: 220, overflowY: 'auto', background: '#fff', border: '1px solid #D1F0E0', borderRadius: 10, boxShadow: '0 8px 24px rgba(27,94,59,0.12)', padding: '4px 0' }}>
          {options.map(opt => (
            <div key={opt}
              onMouseDown={() => { onChange(opt); setOpen(false); }}
              style={{ padding: '7px 12px', fontSize: 12, color: opt === value ? '#1B5E3B' : '#1a1a2e', background: opt === value ? '#E8F6EE' : 'transparent', fontWeight: opt === value ? 600 : 400, cursor: 'pointer', transition: 'background 0.1s' }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = '#F0FAF4'; }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddEnumForm({ onSave }) {
  const [tables, setTables] = useState([]);
  const [f, setF] = useState({ table_name: '', column_name: '', value: '', meaning: '', value_type: 'fixed' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminAPI.tableDefs.list().then(r => setTables((r.data.tables || []).map(t => t.table_name || t.name).filter(Boolean))); }, []);
  const inp = { padding: '7px 10px', borderRadius: 8, border: '1px solid #D1F0E0', fontSize: 12, color: '#1a1a2e', background: '#F8FEFC', outline: 'none', height: 34 };
  const save = async () => {
    if (!f.table_name || !f.column_name || !f.value) return;
    setSaving(true);
    await adminAPI.columnEnums.upsert(f);
    setF({ table_name: '', column_name: '', value: '', meaning: '', value_type: 'fixed' });
    setSaving(false); onSave();
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <div style={S.field}>
        <label style={S.flabel}>Table</label>
        <ThemedSelect
          value={f.table_name}
          onChange={val => setF(p => ({ ...p, table_name: val }))}
          options={tables}
          placeholder="Select table"
          minWidth={170}
        />
      </div>
      <div style={S.field}>
        <label style={S.flabel}>Column</label>
        <input style={inp} value={f.column_name} onChange={e => setF(p => ({ ...p, column_name: e.target.value }))} placeholder="e.g. QtnStage" />
      </div>
      <div style={S.field}>
        <label style={S.flabel}>Value</label>
        <input style={inp} value={f.value} onChange={e => setF(p => ({ ...p, value: e.target.value }))} placeholder="e.g. Active" />
      </div>
      <div style={S.field}>
        <label style={S.flabel}>Meaning</label>
        <input style={{ ...inp, minWidth: 200 }} value={f.meaning} onChange={e => setF(p => ({ ...p, meaning: e.target.value }))} placeholder="e.g. Quotation is active / open" />
      </div>
      <button style={{ ...S.saveBtn, padding: '7px 16px', fontSize: 12 }} onClick={save} disabled={saving}>{saving ? <Spinner small /> : '+ Add'}</button>
    </div>
  );
}

// ── Playground ────────────────────────────────────────────────────────────

function PlaygroundSection() {
  const [query, setQuery] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');

  const test = async () => {
    if (!query.trim()) return;
    setTesting(true); setResult(null); setError('');
    try {
      const r = await adminAPI.playground.test(query.trim());
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Test failed.');
    }
    setTesting(false);
  };

  const confColor = (c) => c >= 0.75 ? '#059669' : c >= 0.4 ? '#d97706' : '#dc2626';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={S.card}>
        <div style={S.cardTitle}>Test Query Routing</div>
        <div style={S.cardSub}>Type any question to see how the AI pipeline processes it — intent, module match, confidence, and generated SQL. No real DB execution.</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <input
            value={query} onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && test()}
            style={{ flex: 1, padding: '10px 14px', borderRadius: 9, border: '1px solid #D1F0E0', fontSize: 14, color: '#1a1a2e', background: '#F8FEFC', outline: 'none' }}
            placeholder='e.g. "Show top 10 customers by revenue this quarter"'
          />
          <button style={{ ...S.saveBtn, padding: '10px 22px' }} onClick={test} disabled={testing}>
            {testing ? <Spinner small /> : '▶'} Test
          </button>
        </div>
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}

      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {[
              { label: 'Intent', val: result.intent || '—', color: '#1B5E3B' },
              { label: 'Intent Confidence', val: result.intent_confidence != null ? `${(result.intent_confidence * 100).toFixed(0)}%` : '—', color: confColor(result.intent_confidence) },
              { label: 'Semantic Confidence', val: result.semantic_confidence != null ? `${(result.semantic_confidence * 100).toFixed(0)}%` : '—', color: confColor(result.semantic_confidence) },
              { label: 'Matched Module', val: result.matched_module || 'None', color: result.matched_module ? '#1B5E3B' : '#9b9b9b' },
            ].map(kpi => (
              <div key={kpi.label} style={{ ...S.card, padding: '14px 16px' }}>
                <div style={{ fontSize: 11, color: '#9b9b9b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{kpi.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: kpi.color }}>{kpi.val}</div>
              </div>
            ))}
          </div>

          {(result.injected_tables || []).length > 0 && (
            <div style={S.card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Tables Used</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {result.injected_tables.map(t => <span key={t} style={{ padding: '3px 10px', borderRadius: 6, background: '#E8F6EE', color: '#1B5E3B', fontSize: 12, fontWeight: 700 }}>{t}</span>)}
              </div>
            </div>
          )}

          {result.sql && (
            <div style={S.card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Generated SQL</div>
              <pre style={{ margin: 0, padding: '12px 14px', background: '#071A0E', color: '#4CAF7D', borderRadius: 8, fontSize: 12, overflowX: 'auto', lineHeight: 1.6, border: '1px solid #1A4A2E' }}>
                {result.sql}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Integrations (Telegram + WhatsApp) ───────────────────────────────────

function IntegrationsSection() {
  const [tgToken,    setTgToken]    = useState('');
  const [tgStatus,   setTgStatus]   = useState(null);
  const [tgSaving,   setTgSaving]   = useState(false);
  const [tgStopping, setTgStopping] = useState(false);
  const [tgShow,     setTgShow]     = useState(false);

  const [waStatus,   setWaStatus]   = useState(null);
  const [waForm,     setWaForm]     = useState({ phoneNumberId: '', accessToken: '', verifyToken: '' });
  const [waSaving,   setWaSaving]   = useState(false);
  const [waShow,     setWaShow]     = useState(false);

  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const flash = (text, ok = true) => {
    if (ok) { setMsg(text); setErr(''); } else { setErr(text); setMsg(''); }
    setTimeout(() => { setMsg(''); setErr(''); }, 4000);
  };

  useEffect(() => {
    adminAPI.telegram.getConfig()
      .then(r => setTgStatus(r.data))
      .catch(() => setTgStatus({ configured: false, running: false }));
    adminAPI.whatsapp.getConfig()
      .then(r => setWaStatus(r.data))
      .catch(() => setWaStatus({ configured: false, running: false }));
  }, []);

  // ── Telegram handlers ──
  const tgSave = async () => {
    if (!tgToken.trim()) { flash('Paste your bot token first.', false); return; }
    setTgSaving(true);
    try {
      const r = await adminAPI.telegram.setToken(tgToken.trim());
      flash(r.data.message);
      setTgStatus({ configured: true, running: true });
      setTgToken(''); setTgShow(false);
    } catch (e) { flash(e.response?.data?.error || 'Failed to start bot.', false); }
    finally { setTgSaving(false); }
  };
  const tgStop = async () => {
    if (!window.confirm('Stop the Telegram bot and remove the token?')) return;
    setTgStopping(true);
    try {
      const r = await adminAPI.telegram.stopBot();
      flash(r.data.message);
      setTgStatus({ configured: false, running: false });
    } catch (e) { flash(e.response?.data?.error || 'Failed to stop bot.', false); }
    finally { setTgStopping(false); }
  };

  // ── WhatsApp handlers ──
  const waSave = async () => {
    const { phoneNumberId, accessToken, verifyToken } = waForm;
    if (!phoneNumberId.trim() || !accessToken.trim() || !verifyToken.trim()) {
      flash('All three fields are required.', false); return;
    }
    setWaSaving(true);
    try {
      const r = await adminAPI.whatsapp.configure(waForm);
      flash(r.data.message);
      setWaStatus({ configured: true, running: true, phoneNumberId: phoneNumberId.trim(), verifyToken: verifyToken.trim() });
      setWaShow(false);
    } catch (e) { flash(e.response?.data?.error || 'Failed to save WhatsApp config.', false); }
    finally { setWaSaving(false); }
  };
  const waDisable = async () => {
    if (!window.confirm('Disable WhatsApp integration and remove all credentials?')) return;
    try {
      const r = await adminAPI.whatsapp.disable();
      flash(r.data.message);
      setWaStatus({ configured: false, running: false });
      setWaShow(false);
    } catch (e) { flash(e.response?.data?.error || 'Failed to disable WhatsApp.', false); }
  };

  const tgDot = tgStatus?.running ? '#2E7D52' : tgStatus?.configured ? '#E65100' : '#9E9E9E';
  const tgDotLabel = tgStatus?.running ? 'Running' : tgStatus?.configured ? 'Configured' : 'Not configured';
  const waDot = waStatus?.running ? '#2E7D52' : waStatus?.configured ? '#E65100' : '#9E9E9E';
  const waDotLabel = waStatus?.running ? 'Active' : waStatus?.configured ? 'Configured' : 'Not configured';

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0A3D1F', marginBottom: 4 }}>📡 Integrations</div>
        <div style={{ fontSize: 13, color: '#555' }}>Connect your ERP assistant to external platforms. Users get the same AI answers as the web portal.</div>
      </div>

      {msg && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#E8F6EE', borderRadius: 8, fontSize: 12, color: '#1B5E3B', fontWeight: 600 }}>{msg}</div>}
      {err && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FFEBEE', borderRadius: 8, fontSize: 12, color: '#B71C1C', fontWeight: 600 }}>{err}</div>}

      {/* ── Telegram card ── */}
      <div style={{ background: '#fff', border: '1px solid #C8EDD6', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 28 }}>✈️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0A3D1F' }}>Telegram Bot</div>
            <div style={{ fontSize: 12, color: '#555' }}>Users query the ERP directly from Telegram. Hindi + English, Excel attachments, /format command.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: tgDot }} />
            <span style={{ color: tgDot }}>{tgDotLabel}</span>
          </div>
        </div>

        <div style={{ background: '#F3F7FC', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: '#333', lineHeight: 1.7 }}>
          <strong>How to get your bot token:</strong><br />
          1. Open Telegram → search <code style={{ background: '#E0EDF5', padding: '1px 5px', borderRadius: 4 }}>@BotFather</code><br />
          2. Send <code style={{ background: '#E0EDF5', padding: '1px 5px', borderRadius: 4 }}>/newbot</code> → choose a name + username ending in <code style={{ background: '#E0EDF5', padding: '1px 5px', borderRadius: 4 }}>bot</code><br />
          3. BotFather replies with a token like <code style={{ background: '#E0EDF5', padding: '1px 5px', borderRadius: 4 }}>1234567890:AAHdq...</code><br />
          4. Paste it below and click <strong>Start Bot</strong>
        </div>

        {tgStatus?.running ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 13, color: '#1B5E3B', fontWeight: 600 }}>✅ Bot is active and accepting messages</div>
            <button style={{ background: '#B71C1C', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onClick={tgStop} disabled={tgStopping}>
              {tgStopping ? 'Stopping…' : '⏹ Stop Bot'}
            </button>
          </div>
        ) : (
          <div>
            {!tgShow ? (
              <button style={{ ...S.saveBtn, fontSize: 13 }} onClick={() => setTgShow(true)}>
                {tgStatus?.configured ? '🔄 Replace Token' : '▶ Connect Telegram'}
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>Paste bot token from BotFather:</div>
                <input type="password" value={tgToken} onChange={e => setTgToken(e.target.value)}
                  placeholder="1234567890:AAHdqTcvCH1vGWJxfSe..."
                  style={{ ...S.finput, fontFamily: 'monospace', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...S.saveBtn, fontSize: 13 }} onClick={tgSave} disabled={tgSaving}>
                    {tgSaving ? 'Starting…' : '▶ Start Bot'}
                  </button>
                  <button style={{ background: '#E8F6EE', color: '#1B5E3B', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
                    onClick={() => { setTgShow(false); setTgToken(''); }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── WhatsApp card ── */}
      <div style={{ background: '#fff', border: '1px solid #C8EDD6', borderRadius: 12, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 28 }}>💬</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0A3D1F' }}>WhatsApp</div>
            <div style={{ fontSize: 12, color: '#555' }}>Meta Cloud API — users query the ERP via WhatsApp. Same pipeline, format options, approval flow.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: waDot }} />
            <span style={{ color: waDot }}>{waDotLabel}</span>
          </div>
        </div>

        <div style={{ background: '#F3F7FC', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: '#333', lineHeight: 1.7 }}>
          <strong>Setup steps (Meta Cloud API):</strong><br />
          1. Go to <strong>Meta for Developers</strong> → create a WhatsApp Business App<br />
          2. Add a test phone number → copy the <strong>Phone Number ID</strong><br />
          3. Generate a <strong>Temporary / Permanent Access Token</strong><br />
          4. Choose any string as your <strong>Verify Token</strong> (e.g. <code style={{ background: '#E0EDF5', padding: '1px 5px', borderRadius: 4 }}>mySecret123</code>)<br />
          5. Set the webhook URL in Meta dashboard to:<br />
          <code style={{ background: '#E0EDF5', padding: '2px 6px', borderRadius: 4, display: 'inline-block', marginTop: 4 }}>https://your-domain.com/api/whatsapp/webhook</code><br />
          <span style={{ color: '#888', fontSize: 11 }}>For local dev use ngrok: <code style={{ background: '#E0EDF5', padding: '1px 4px', borderRadius: 3 }}>ngrok http 8001</code></span>
        </div>

        {waStatus?.configured && !waShow ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 13, color: '#1B5E3B', fontWeight: 600 }}>✅ WhatsApp integration is active</div>
            <button style={{ ...S.saveBtn, fontSize: 12 }} onClick={() => setWaShow(true)}>🔄 Update Config</button>
            <button style={{ background: '#B71C1C', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onClick={waDisable}>Disable</button>
          </div>
        ) : (
          <div>
            {!waShow ? (
              <button style={{ ...S.saveBtn, fontSize: 13 }} onClick={() => setWaShow(true)}>
                ▶ Connect WhatsApp
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  ['Phone Number ID', 'phoneNumberId', 'text',     '1234567890123456'],
                  ['Access Token',    'accessToken',   'password', 'EAAxxxxxxxxxxxxx…'],
                  ['Verify Token',    'verifyToken',   'text',     'mySecret123'],
                ].map(([label, key, type, ph]) => (
                  <div key={key}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 4 }}>{label}</div>
                    <input type={type} value={waForm[key]}
                      onChange={e => setWaForm(f => ({ ...f, [key]: e.target.value }))}
                      placeholder={ph}
                      style={{ ...S.finput, fontFamily: key === 'accessToken' ? 'monospace' : undefined, fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button style={{ ...S.saveBtn, fontSize: 13 }} onClick={waSave} disabled={waSaving}>
                    {waSaving ? 'Saving…' : '💾 Save & Activate'}
                  </button>
                  <button style={{ background: '#E8F6EE', color: '#1B5E3B', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
                    onClick={() => setWaShow(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Coming Soon placeholder ───────────────────────────────────────────────

function ComingSoon({ title, desc, icon }) {
  return (
    <div style={S.comingSoonWrap}>
      <div style={S.comingSoonIcon}>{icon}</div>
      <div style={S.comingSoonTitle}>{title}</div>
      <div style={S.comingSoonDesc}>{desc}</div>
      <div style={S.comingSoonBadge}>Coming Next</div>
    </div>
  );
}

// ── Main Admin page ───────────────────────────────────────────────────────

export default function Admin() {
  const [tab, setTab] = useState('config');
  const current = TABS.find(t => t.id === tab);

  return (
    <div style={S.page}>
      {/* Page header */}
      <div style={S.pageHeader}>
        <div>
          <div style={S.pageTitle}>Admin Panel</div>
          <div style={S.pageSub}>Manage model settings, users, and system configuration</div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={S.tabBar}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            ...S.tabBtn,
            ...(tab === t.id ? S.tabBtnActive : {})
          }}>
            <span>{t.icon}</span>
            {t.label}
            {tab === t.id && <div style={S.tabUnderline} />}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={S.content}>
        {tab === 'config'    && <ModelConfigTab />}
        {tab === 'users'     && <UserManagementTab />}
        {tab === 'analytics' && <AnalyticsTab />}
        {tab === 'security'  && <SecurityTab />}
        {tab === 'schema'    && <SchemaTab />}
        {tab === 'database'  && <DatabaseTab onGoToSchema={() => setTab('schema')} />}
        {tab === 'ai-config' && <AIConfigTab />}
      </div>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────

function Spinner({ small }) {
  return <span style={{ width: small ? 12 : 16, height: small ? 12 : 16, borderRadius: '50%', border: '2px solid rgba(27,94,59,0.25)', borderTopColor: '#1B5E3B', display: 'inline-block', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />;
}

// ── Styles ────────────────────────────────────────────────────────────────

const S = {
  page:       { padding: '0 0 40px', minHeight: '100vh', background: '#EAF7F7' },
  pageHeader: { padding: '28px 32px 0', marginBottom: 0 },
  pageTitle:  { fontSize: 22, fontWeight: 700, color: '#1a1a2e' },
  pageSub:    { fontSize: 13, color: '#9b9b9b', marginTop: 4 },

  tabBar: {
    display: 'flex', gap: 0, padding: '16px 32px 0',
    borderBottom: '1px solid #E0F5EE', marginBottom: 0,
    background: '#EAF7F7'
  },
  tabBtn: {
    display: 'flex', alignItems: 'center', gap: 7, position: 'relative',
    padding: '10px 16px 12px', border: 'none', background: 'transparent',
    color: '#9b9b9b', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    transition: 'color 0.18s', whiteSpace: 'nowrap'
  },
  tabBtnActive: { color: '#1B5E3B', fontWeight: 600 },
  tabUnderline: {
    position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
    background: 'linear-gradient(90deg, #1B5E3B, #2E7D52)', borderRadius: '2px 2px 0 0'
  },

  content: { padding: '28px 32px' },

  configWrap:   { maxWidth: 900 },
  sectionHeader:{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' },
  sectionTitle: { fontSize: 17, fontWeight: 700, color: '#1a1a2e' },
  sectionSub:   { fontSize: 13, color: '#9b9b9b', marginTop: 4 },

  saveBtn: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 24px', borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 28%, #2E7D52 52%, #1B5E3B 76%, #0A2E17 100%)',
    color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(27,94,59,0.42), inset 0 1px 0 rgba(255,255,255,0.12)', whiteSpace: 'nowrap'
  },

  errorBanner:  { background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 10, padding: '12px 16px', color: '#dc2626', fontSize: 13, marginBottom: 16 },
  successBanner:{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)', borderRadius: 10, padding: '12px 16px', color: '#059669', fontSize: 13, marginBottom: 16 },

  grid2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  card:   { background: '#fff', borderRadius: 14, padding: '20px 22px', border: '1px solid #D1F0E0', boxShadow: '0 2px 8px rgba(27,94,59,0.04)' },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 },
  cardSub:   { fontSize: 12, color: '#9b9b9b', lineHeight: 1.5 },
  valueChip: { fontSize: 13, fontWeight: 700, color: '#1B5E3B', background: '#E8F6EE', border: '1px solid #C8EDD6', borderRadius: 6, padding: '1px 8px' },

  modelGrid:       { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 },
  modelOption:     { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 10, border: '1.5px solid #e8e6f0', cursor: 'pointer', transition: 'all 0.18s', flex: '1 1 200px' },
  modelOptionActive:{ borderColor: '#1B5E3B', background: '#E8F6EE', boxShadow: '0 0 0 3px rgba(27,94,59,0.1)' },
  modelRadio:      { width: 18, height: 18, borderRadius: '50%', border: '2px solid #d4d0e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  modelDot:        { width: 8, height: 8, borderRadius: '50%', background: 'transparent', transition: 'background 0.15s' },
  modelDotActive:  { background: '#1B5E3B' },
  modelLabel:      { fontSize: 13, fontWeight: 600, color: '#1a1a2e' },
  modelDesc:       { fontSize: 11, color: '#9b9b9b' },

  slider:       { width: '100%', accentColor: '#1B5E3B', cursor: 'pointer' },
  sliderLabels: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#c0bdb8', marginTop: 4 },
  presetRow:    { display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' },
  presetBtn:    { padding: '4px 12px', borderRadius: 7, border: '1px solid #e8e6f0', background: '#f2faf8', color: '#6b7280', fontSize: 12, cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s' },
  presetBtnActive:{ borderColor: '#1B5E3B', background: '#E8F6EE', color: '#1B5E3B', fontWeight: 700 },

  toggleRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 },
  toggle:     { width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.25s', padding: 0, flexShrink: 0 },
  toggleThumb:{ position: 'absolute', top: 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'transform 0.25s' },

  keyRow:   { display: 'flex', gap: 8, marginTop: 12 },
  keyInput: { flex: 1, padding: '10px 12px', borderRadius: 9, border: '1px solid #e8e6f0', fontSize: 13, color: '#1a1a2e', background: '#f2faf8', outline: 'none' },
  eyeBtn:   { padding: '0 12px', border: '1px solid #e8e6f0', borderRadius: 9, background: '#f2faf8', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  keyHint:  { fontSize: 11, color: '#059669', marginTop: 6, fontWeight: 500 },

  center:  { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 },
  field:   { display: 'flex', flexDirection: 'column', gap: 5 },
  flabel:  { fontSize: 12, fontWeight: 500, color: '#6b7280' },
  finput:  { padding: '9px 12px', borderRadius: 9, border: '1px solid #e8e6f0', fontSize: 13, color: '#1a1a2e', background: '#f2faf8', outline: 'none' },
  actBtn:  { padding: '4px 10px', borderRadius: 7, border: '1px solid #e8e6f0', background: '#fff', fontSize: 11, fontWeight: 600, color: '#1B5E3B', cursor: 'pointer' },
  cancelBtn:{ padding: '10px 20px', borderRadius: 10, border: '1px solid #e8e6f0', background: '#fff', fontSize: 14, fontWeight: 500, color: '#6b7280', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 16, padding: 28, width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },

  comingSoonWrap:  { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 32px', textAlign: 'center' },
  comingSoonIcon:  { fontSize: 52, marginBottom: 20 },
  comingSoonTitle: { fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 },
  comingSoonDesc:  { fontSize: 14, color: '#6b7280', maxWidth: 420, lineHeight: 1.6, marginBottom: 24 },
  comingSoonBadge: { padding: '8px 24px', borderRadius: 20, background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)', color: '#fff', fontSize: 13, fontWeight: 600 },
};
